import { html, css, LitElement } from "lit";
import { customElement } from "lit/decorators.js";
import { property, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import type { EadFmNodeRun } from "../../types/ead-types.js";
import { hermesClient } from "../hermes-singleton.js";
import { runGatewayPoll } from "../gateway-poll-queue.ts";
import { canPollGateway } from "../poll-coordinator.ts";
import type { ParsedRunReport } from "../pfm-report-parser.ts";
import { parsePfmNodeListResponse } from "../pfm-node-list-parser.ts";
import type { ParsedPfmNodeList } from "../pfm-node-list-parser.ts";
import {
  loadPendingPfmRequest,
  savePendingPfmRequest,
  clearPendingPfmRequest,
  loadPfmMindmap,
  savePfmMindmap,
} from "../../local-storage.ts";
import {
  buildMindmapCacheKey,
  collectCenterHubKeysFromFlat,
  appendDrillPathKey,
  resolveCommittedNodeKeyInTree,
  stripPfmDisplayTitle,
  type DrillAncestor,
} from "../pfm-drill-keys.ts";
import {
  loadPfmMindmapRemote,
  persistPfmMindmapRemote,
} from "../pfm-mindmap-remote-cache.ts";
import {
  clearCommittedMindmapScopeCacheForExecution,
  getCommittedMindmapScopeCache,
  setCommittedMindmapScopeCache,
} from "../pfm-committed-mindmap-cache.ts";
import {
  getPersistedCommittedMindmapScopeCache,
} from "../pfm-committed-mindmap-session-cache.ts";
import { renderMermaidToSvg } from "../mermaid-renderer.ts";
import { icons } from "../icons.js";
import {
  formatPfmMindmapHeaderFromExecution,
  formatPfmMindmapDisplayLabel,
  resolveClientPfmLineage,
  type PfmLineageRunRef,
} from "../pfm-version-label.ts";
import {
  parsePfmTreeListPayload,
  renderPfmTreeListPanel,
  type PfmTreeListPayload,
} from "../pfm-tree-list.ts";

type MermaidMapPayload = {
  map: { name: string } | null;
  nodes: Array<{
    node_id: number;
    parent_node_id: number | null;
    node_key: string;
    title: string;
  }>;
  features: Array<Record<string, unknown>>;
  testCases: Array<Record<string, unknown>>;
  testSteps: Array<Record<string, unknown>>;
};

function parentPathKey(path: DrillAncestor[] | undefined | null): string {
  return (path || [])
    .map((item) => `${String(item?.nodeKey || "").trim()}:${String(item?.title || "").trim()}`)
    .join("|");
}

function stringArrayKey(values: string[] | undefined | null): string {
  return (values || []).map((v) => String(v || "").trim()).join("|");
}
const NODE_LIST_PROMPT =
  [
    "Return the full hierarchical PFM node tree (all levels), not top-level nodes only.",
    "",
    "When studying this application, focus on product functions for the PFM mindmap.",
    "Sample data may showcase the UI but must not mislead you: the map is about product functions, not that sample data.",
    "In the full PFM mindmap and related artifacts, use multiple hierarchy levels under these top nodes (not a single flat layer) so readers can go from general areas to specific features.",
    "At each parent node, include at most 15 direct children (siblings). If you find more, add an intermediate grouping level.",
    "Do not list table columns, filter chips, or pagination as separate mindmap nodes — put that detail in EAD reports.",
    "",
    "Rules:",
    "- Output one node per line.",
    "- Number each node starting from 1.",
    "- Each line must follow this shape (use your own keys and titles from this product—do not copy placeholder text):",
    "  <number>. <Node Key> - <Short Human Readable Title>",
    "- The Node Key should be short and stable, using lowercase words separated by hyphens.",
    "- The Short Human Readable Title should use normal spaces between words.",
    "- Do not use markdown tables.",
    "- Do not include headers, explanations, summaries, bullets, or extra commentary beyond the numbered lines.",
    "- Name nodes from product functions you infer from exploration, not from sample rows, demo labels, placeholder text, or one-off values on screen.",
    "- When you assert or re-confirm a node or feature in exploration, capture a screenshot and record it with the same reporting step so evidence stays attached.",
  ].join("\n");

const REQUEST_MARKER_RE = /\[Request-Id:\s*(REQ-[A-Z0-9-]+)\s*\]/i;
const REPLY_MARKER_RE = /\[Reply-To:\s*(REQ-[A-Z0-9-]+)\s*\]/i;
const MAX_WAIT_MS = 5 * 60 * 1000;
const WATCHER_INTERVAL_MS = 8000;
function generateReqId(): string {
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase().padEnd(4, "0");
  return `REQ-${ts}-${rand}`;
}

function buildMarkeredPrompt(reqId: string): string {
  return [
    `[Request-Id: ${reqId}]`,
    NODE_LIST_PROMPT,
    "",
    "IMPORTANT: Begin your reply with this exact line so the UI can match it:",
    `[Reply-To: ${reqId}]`,
  ].join("\n");
}

function buildWakePrompt(reqId: string): string {
  return [
    `Please process the immediately previous request [Request-Id: ${reqId}].`,
    `Begin your answer with this exact line: [Reply-To: ${reqId}]`,
  ].join("\n");
}

function stripReplyMarker(text: string): string {
  return String(text || "").replace(REPLY_MARKER_RE, "").trim();
}

/**
 * PFM feature map (mindmap) for EAD Explore runs.
 *
 * ── DO NOT REGRESS (repeated mistake) ─────────────────────────────────────
 * When `executionId` is set (always true in Run Explore), the map is DB-READ ONLY:
 *   • Table: ``pfm_nodes`` — promoted scalar columns only (no JSON snapshot).
 *   • Load/display/reload/drill-down → gateway `pfm.mindmap` only (`_renderCommittedScope`).
 *   • Manual refresh → `reloadFromDb()` only.
 *
 * NEVER on this path (not on node-report save, not on tab switch, not on “reload from DB”):
 *   • `pfm.requestSnapshot` / refreshFromDelivery / .pfm delivery import
 *   • Agent chat regenerate (`_regenerateMindmap`) or rebuilding the PFM node list
 *   • Clearing the scope cache to “force” a rebuild
 *
 * Node reports are separate (`pfmNodeReports.*`). Saving a report does not change the map in DB.
 * Legacy chat-only helpers below (`_loadSavedMindmap`, `_regenerateMindmap`) run only when
 * `executionId` is empty — not used by Run Explore.
 * ─────────────────────────────────────────────────────────────────────────
 */
@customElement("ead-pfm-mindmap-mermaid")
export class EadPfmMindmapMermaid extends LitElement {
  static styles = css`
    :host {
      display: block;
      border: 1px solid color-mix(in srgb, var(--border) 78%, transparent);
      border-radius: var(--radius-md, 10px);
      background: color-mix(in srgb, var(--card, #161920) 86%, transparent);
      min-height: 200px;
      max-width: 100%;
    }

    .canvas {
      padding: 12px;
      overflow: auto;
      max-width: 100%;
      box-sizing: border-box;
    }

    .toolbar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      margin-bottom: 8px;
    }

    .meta-card {
      min-width: 0;
      flex: 1 1 auto;
      padding: 10px 12px;
      border: 1px solid color-mix(in srgb, var(--border) 72%, transparent);
      border-radius: 8px;
      background: color-mix(in srgb, var(--bg-elevated, #161920) 90%, transparent);
    }

    .meta-title {
      font-size: 12px;
      font-weight: 700;
      color: var(--text-strong);
      margin-bottom: 4px;
    }

    .meta-description {
      font-size: 12px;
      color: var(--muted);
      line-height: 1.45;
    }

    .btn {
      padding: 6px 10px;
      border-radius: var(--radius-md, 10px);
      border: 1px solid color-mix(in srgb, var(--border) 88%, transparent);
      background: color-mix(in srgb, var(--bg-elevated, #1b1f2a) 80%, transparent);
      color: var(--text-strong, #f4f4f5);
      font-size: 11px;
      font-weight: 600;
      cursor: pointer;
      white-space: nowrap;
    }

    .btn:hover:not(:disabled) {
      background: var(--bg-hover, #232733);
    }

    .btn:disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }

    .icon-actions {
      margin-left: auto;
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }

    .icon-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
      border-radius: 8px;
      border: 1px solid color-mix(in srgb, var(--border) 88%, transparent);
      background: color-mix(in srgb, var(--bg-elevated, #1b1f2a) 80%, transparent);
      color: var(--text-strong, #f4f4f5);
      cursor: pointer;
      padding: 0;
    }

    .icon-btn:hover:not(:disabled) {
      background: var(--bg-hover, #232733);
    }

    .icon-btn:disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }

    .icon-btn > span {
      display: inline-flex;
      width: 16px;
      height: 16px;
      align-items: center;
      justify-content: center;
    }

    .icon-btn svg {
      width: 16px;
      height: 16px;
      stroke: currentColor;
      stroke-width: 2;
      fill: none;
    }

    .icon-spin {
      animation: ead-spin 0.8s linear infinite;
    }

    @keyframes ead-spin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }

    .canvas--maximized {
      position: fixed;
      inset: 14px;
      z-index: 2600;
      border: 1px solid color-mix(in srgb, var(--border) 78%, transparent);
      border-radius: 12px;
      background: color-mix(in srgb, var(--card, #161920) 96%, #0b0e14 4%);
      box-shadow: 0 24px 80px rgba(0, 0, 0, 0.55);
      padding: 14px;
    }

    .canvas--maximized .svg-wrap {
      min-height: calc(100vh - 170px);
    }

    .svg-wrap {
      width: 100%;
      min-width: 0;
      background: #181818;
      border-radius: 8px;
      padding: 8px;
      box-sizing: border-box;
      overflow: auto;
    }

    .svg-wrap--dimmed {
      opacity: 0.35;
      pointer-events: none;
      filter: blur(0.4px);
      transition: opacity 0.2s ease;
    }

    .scope-transition {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      min-height: 220px;
      margin: 0 0 8px;
      padding: 28px 20px;
      border-radius: 10px;
      border: 1px dashed color-mix(in srgb, var(--accent, #7c6cff) 35%, var(--border));
      background: color-mix(in srgb, var(--accent, #7c6cff) 6%, var(--card));
      color: var(--text-strong);
      font-size: 13px;
      line-height: 1.5;
      text-align: center;
    }

    .mindmap-stage {
      position: relative;
    }

    .scope-transition--overlay {
      position: absolute;
      inset: 0;
      z-index: 2;
      margin: 0;
      min-height: 0;
      background: color-mix(in srgb, var(--card) 88%, transparent);
      backdrop-filter: blur(2px);
    }

    .scope-transition--overlay .scope-transition__spinner {
      color: var(--accent, #7c6cff);
    }

    .scope-transition__spinner {
      display: inline-flex;
      width: 22px;
      height: 22px;
      flex-shrink: 0;
    }

    .svg-wrap :is(g, .node, .mindmap-node) {
      cursor: pointer;
    }

    .svg-wrap g.node--visited-dim {
      opacity: 0.5;
    }

    .svg-wrap g.node--active-path {
      opacity: 1 !important;
      filter: drop-shadow(0 0 2px rgba(196, 215, 255, 0.45));
    }

    .svg-wrap g.node--active-path :is(rect, circle, ellipse, polygon, path) {
      stroke: #b9cbff !important;
      stroke-width: 2px !important;
    }

    .svg-wrap g.node--active-path text {
      fill: #f3f7ff !important;
      font-weight: 700 !important;
    }

    .svg-wrap :is(text, tspan).pfm-leaf-text--selected {
      fill: #fef9c3 !important;
      font-weight: 700 !important;
    }

    .map-header {
      margin: 0 0 8px;
    }

    .map-title-row {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px;
      font-size: 11px;
      color: var(--muted);
    }

    .map-title {
      font-weight: 700;
      color: var(--text-strong);
    }

    .map-leaf-subtitle {
      margin-top: 4px;
      font-size: 12px;
      line-height: 1.4;
      color: color-mix(in srgb, var(--muted) 92%, var(--text-strong, #f4f4f5));
      animation: map-leaf-subtitle-in 0.22s ease-out;
    }

    .map-leaf-subtitle--out {
      animation: map-leaf-subtitle-out 0.45s ease-in forwards;
    }

    @keyframes map-leaf-subtitle-in {
      from {
        opacity: 0;
        transform: translateY(-3px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes map-leaf-subtitle-out {
      from {
        opacity: 1;
      }
      to {
        opacity: 0;
      }
    }

    .svg-wrap :is(svg, svg#my-svg) {
      width: 100% !important;
      max-width: 100%;
      height: auto !important;
      display: block;
    }

    .svg-wrap :is(path.edge, path.edge-depth-0, path.edge-depth-1, path.edge-depth-2, path.edge-depth-3, .edge, .link, line) {
      stroke: #7a83d8 !important;
      stroke-width: 1.25px !important;
      fill: none !important;
    }

    .state {
      min-height: 180px;
      display: grid;
      place-items: center;
      color: var(--muted);
      font-size: 13px;
      text-align: center;
      padding: 20px;
    }

    .state--error {
      color: #fca5a5;
    }

    .leaf-state {
      color: color-mix(in srgb, var(--text-strong, #f4f4f5) 88%, transparent);
      line-height: 1.6;
      max-width: 60ch;
    }

    .leaf-title {
      font-size: 22px;
      font-weight: 700;
      color: var(--text-strong, #f4f4f5);
      margin-bottom: 8px;
      letter-spacing: 0.01em;
    }

    .leaf-description {
      font-size: 14px;
      color: color-mix(in srgb, var(--text, #d4d4d8) 92%, transparent);
    }

    .pfm-treelist-wrap {
      margin-top: 12px;
      border: 1px solid color-mix(in srgb, var(--border) 80%, transparent);
      border-radius: 10px;
      background: color-mix(in srgb, var(--surface-2, #18181b) 92%, transparent);
      max-height: 320px;
      overflow: auto;
      text-align: left;
    }

    .pfm-treelist {
      font-family: var(--mono, ui-monospace, monospace);
      font-size: 11px;
      line-height: 1.55;
      padding: 10px 12px;
    }

    .pfm-treelist__summary {
      display: flex;
      flex-direction: column;
      gap: 4px;
      margin-bottom: 8px;
      color: color-mix(in srgb, var(--muted) 90%, var(--text-strong, #f4f4f5));
    }

    .pfm-treelist__summary strong {
      color: var(--text-strong, #f4f4f5);
      font-size: 12px;
    }

    .pfm-treelist__alert,
    .pfm-treelist__warn-block {
      color: #fca5a5;
      font-size: 11px;
      line-height: 1.45;
    }

    .pfm-treelist__roots,
    .pfm-treelist__children {
      list-style: none;
      margin: 0;
      padding: 0;
    }

    .pfm-treelist__row {
      display: flex;
      align-items: baseline;
      gap: 6px;
      padding: 1px 0;
    }

    .pfm-treelist__item--crowded > .pfm-treelist__row .pfm-treelist__title {
      color: #fca5a5;
    }

    .pfm-treelist__title {
      color: var(--text, #e4e4e7);
      flex: 1 1 auto;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .pfm-treelist__meta {
      color: var(--muted);
      font-size: 10px;
      white-space: nowrap;
    }

    .pfm-treelist__warn {
      color: #fca5a5;
      font-size: 10px;
      font-weight: 600;
    }

    .pfm-treelist__branch,
    .pfm-treelist__leaf {
      color: color-mix(in srgb, var(--muted) 85%, var(--accent-2, #14b8a6));
      width: 12px;
      flex-shrink: 0;
    }

    .fallback-map {
      min-width: min(100%, 420px);
      padding: 12px;
      text-align: left;
    }

    .fallback-title {
      margin-bottom: 8px;
      color: var(--text-strong, #f4f4f5);
      font-size: 13px;
      font-weight: 700;
    }

    .fallback-list {
      display: grid;
      gap: 6px;
      margin: 0;
      padding: 0;
      list-style: none;
    }

    .fallback-node {
      padding: 8px 10px;
      border: 1px solid color-mix(in srgb, var(--border) 72%, transparent);
      border-radius: 8px;
      background: color-mix(in srgb, var(--bg-elevated, #161920) 90%, transparent);
      color: var(--text, #d4d4d8);
      font-size: 12px;
      line-height: 1.35;
    }
  `;

  @property({ type: Array }) nodes: EadFmNodeRun[] = [];
  @property({ type: String }) sessionKey = "";
  @property({ type: String }) rootLabel = "";
  @property({ type: String }) leafDescription = "";
  @property({ type: Array }) visitedNodeKeys: string[] = [];
  @property({ type: Array }) activePathNodeKeys: string[] = [];
  /**
   * Agent-authored PFM tree pipeline: when set, the component pulls the
   * canonical committed snapshot from the gateway (GET pfm/tree + pfm/mindmap)
   * instead of waiting for a chat-marker reply. When unset, the legacy
   * chat-marker Generate flow runs.
   */
  @property({ type: String }) executionId = "";
  /** When this run has no committed tree yet, gateway baseline execution to read PFM from. */
  @property({ type: String }) pfmBaselineExecutionId = "";
  /** Resolved execution id with a committed tree to display (from server lineage). */
  @property({ type: String }) pfmTreeReadExecutionId = "";
  @property({ type: Boolean }) pfmHasCommittedSnapshot = false;
  @property({ type: Boolean }) hasDeliveredPfmMapReport = false;
  @property({ type: String }) pfmMapDisplayMode = "";
  @property({ type: Number }) pfmSourceRunNumber = 0;
  @property({ type: Number }) pfmBaselineTreeVersion = 0;
  @property({ type: String }) pfmSnapshotPhase = "";
  @property({ type: Number }) pfmGenerationVersion = 0;
  @property({ type: Number }) currentRunNumber = 0;
  @property({ type: Number }) currentPfmRevision = 0;
  @property({ type: Number }) previousPfmRunNumber = 0;
  @property({ type: Number }) previousPfmRevision = 0;
  /** Pre-resolved V/Rev label from runs view (instant; does not wait on mindmap fetch). */
  @property({ type: String }) pfmHeaderLabel = "";
  @property({ type: Number }) pfmRevision = 0;
  @property({ type: Boolean }) pfmFinalized = false;
  /** Current run status (e.g. running, pending). */
  @property({ type: String }) runStatus = "";
  /**
   * Drill-down ancestors (excluding this mindmap's own root). When empty, this
   * is the top-level mindmap and the existing Generate/Regenerate flow runs.
   * When non-empty, this is a child-level mindmap: it never sends its own
   * request, and instead renders from a SavedPfmMindmap entry that the
   * sibling node-report panel populated as part of its combined reply.
   */
  @property({ attribute: false }) parentPath: DrillAncestor[] = [];
  @property({ attribute: false }) reportData: ParsedRunReport | null = null;
  @property({ type: Boolean }) reportStrict = false;
  @state() private _svg = "";
  @state() private _loading = false;
  @state() private _error = "";
  @state() private _agentPayload: ParsedPfmNodeList | null = null;
  @state() private _agentTitle = "";
  @state() private _agentDescription = "";
  @state() private _warning = "";
  @state() private _showingCachedMindmapView = false;
  private _activeRenderCacheKey = "";
  @state() private _pendingReqId = "";
  @state() private _pendingSentAtSec = 0;
  @state() private _pendingDeadlineMs = 0;
  @state() private _pendingTickMs = 0;
  @state() private _svgRaw = "";
  @state() private _committedStatus: "idle" | "loading" | "committed" | "missing" = "idle";
  @state() private _committedTreeVersion = 0;
  @state() private _committedSourceRunNumber = 0;
  @state() private _committedGeneratedAt = 0;
  @state() private _committedScope: "top" | "focus" = "focus";
  @state() private _committedSelectedNodeKey = "";
  @state() private _highlightedLeafNodeKey = "";
  @state() private _mapLeafSubtitle = "";
  @state() private _mapLeafSubtitleFading = false;
  @state() private _committedNodePath: string[] = [];
  private _mapLeafSubtitleTimer: ReturnType<typeof setTimeout> | null = null;
  private _mapLeafSubtitleFadeTimer: ReturnType<typeof setTimeout> | null = null;
  @state() private _committedMapMaximized = false;
  /** When true, parent (runs view) owns fullscreen; hide internal maximize control. */
  @property({ type: Boolean }) suppressInternalMaximize = false;
  @state() private _drillLoading = false;
  private _drillTransitionNodeKey = "";
  private _activeFocusRequestKey = "";
  private _inflightMindmapCacheKey = "";
  /** Abort in-flight mindmap fetch only when view/read/scope identity changes (not Rev poll). */
  private _inflightMindmapFetchIdentity = "";
  /** Coalesce duplicate scope loads (refresh/Top/click) instead of no-op while in flight. */
  private _mindmapScopeInflight = new Map<string, Promise<void>>();
  private _lastFetchedFocusNodeKey = "";
  private _mindmapFetchAbort: AbortController | null = null;
  @state() private _scopeTransitionMessage = "";
  @state() private _treeListPayload: PfmTreeListPayload | null = null;
  @state() private _pfmTreeReadExecutionId = "";
  private _committedLoadedExecutionId = "";
  private _lastParentPathKey = "";
  private _lastLoadedPfmTreeReadId = "";
  private _lastLoadedPfmBaselineId = "";
  private _lastLoadedPfmDisplayMode = "";
  private _lastLoadedPfmHasSnapshot = false;
  private _lastLoadedPfmStepVersion = 0;
  private _lastLoadedPfmSnapshotPhase = "";
  private _lastVisitedKeysKey = "";
  private _lastActivePathKeysKey = "";
  private _viewStateSaveTimer: ReturnType<typeof setTimeout> | null = null;
  private _committedRenderSeq = 0;
  /** Invalidates in-flight _loadCommittedTree when a newer load starts or aborts. */
  private _committedLoadGeneration = 0;
  private _lastRenderedSignature = "";
  private _committedMermaidRenderSignature = "";
  private _syncFromDbTimer: ReturnType<typeof setTimeout> | null = null;
  /** Stable auto-load key (view run + DB read target + drill path only). */
  private _lastAutoLoadSignature = "";
  private _historyReadForCacheKey: string | null = null;
  private _renderedNodes: Array<{ nodeKey: string; title: string; label: string; index: number }> = [];
  private _watcherTimer: ReturnType<typeof setInterval> | null = null;
  private _countdownTimer: ReturnType<typeof setInterval> | null = null;
  private readonly _onWindowKeydown = (event: KeyboardEvent): void => {
    if (this.suppressInternalMaximize) return;
    if (!this._committedMapMaximized) return;
    if (event.key !== "Escape") return;
    this._committedMapMaximized = false;
    event.preventDefault();
  };

  private _cacheKey(): string {
    return buildMindmapCacheKey(String(this.sessionKey || "").trim(), this.parentPath || []);
  }

  private _isChildLevel(): boolean {
    return Array.isArray(this.parentPath) && this.parentPath.length > 0;
  }

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener("keydown", this._onWindowKeydown);
    if (this.executionId && !this._isChildLevel()) {
      this._lastParentPathKey = parentPathKey(this.parentPath);
      this._committedStatus = "idle";
      this._maybeAutoLoadCommittedMindmap();
    }
    const cacheKey = this._cacheKey();
    if (cacheKey && !this._usingCommittedPath()) {
      void this._loadSavedMindmap();
      if (!this._isChildLevel()) this._resumePendingIfAny();
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener("keydown", this._onWindowKeydown);
    this._stopReplyWatcher();
    this._clearMapLeafSubtitleTimers();
    if (this._viewStateSaveTimer != null) {
      clearTimeout(this._viewStateSaveTimer);
      this._viewStateSaveTimer = null;
    }
  }

  protected willUpdate(changed: Map<string, unknown>): void {
    if (changed.has("executionId")) {
      const next = String(this.executionId || "").trim();
      if (next && next !== this._committedLoadedExecutionId && !this._isChildLevel()) {
        if (this._committedLoadedExecutionId) {
          clearCommittedMindmapScopeCacheForExecution(this._committedLoadedExecutionId);
        }
        clearCommittedMindmapScopeCacheForExecution(next);
        this._lastAutoLoadSignature = "";
        const keepBaselineSvg = Boolean(this._svg) && Boolean(this._baselineReadExecutionId());
        this._committedStatus = keepBaselineSvg ? "committed" : "idle";
        this._committedTreeVersion = 0;
        this._committedGeneratedAt = 0;
        this._committedSelectedNodeKey = "";
        this._highlightedLeafNodeKey = "";
        this._committedNodePath = [];
        this._committedScope = "focus";
        this._committedMapMaximized = false;
        if (!keepBaselineSvg) {
          this._svg = "";
          this._svgRaw = "";
          this._lastRenderedSignature = "";
        }
        this._pfmTreeReadExecutionId = "";
        this._lastParentPathKey = "";
        this._lastLoadedPfmBaselineId = "";
        this._lastLoadedPfmTreeReadId = "";
        this._lastLoadedPfmDisplayMode = "";
        this._lastLoadedPfmHasSnapshot = false;
      }
    }
    if (
      (changed.has("pfmBaselineExecutionId") ||
        changed.has("pfmTreeReadExecutionId") ||
        changed.has("pfmMapDisplayMode") ||
        changed.has("pfmHasCommittedSnapshot") ||
        changed.has("runStatus") ||
        changed.has("pfmSnapshotPhase") ||
        changed.has("pfmRevision") ||
        changed.has("pfmGenerationVersion") ||
        changed.has("currentRunNumber") ||
        changed.has("currentPfmRevision") ||
        changed.has("previousPfmRunNumber") ||
        changed.has("previousPfmRevision")) &&
      !this._isChildLevel() &&
      String(this.executionId || "").trim()
    ) {
      const baseline = String(this.pfmBaselineExecutionId || "").trim();
      const treeRead = String(this.pfmTreeReadExecutionId || "").trim();
      const displayMode = String(this.pfmMapDisplayMode || "").trim().toLowerCase();
      const snap = this.pfmHasCommittedSnapshot === true;
      const step = Math.max(0, Number(this.pfmRevision || 0));
      const phase = String(this.pfmSnapshotPhase || "").trim();
      this._lastLoadedPfmBaselineId = baseline;
      this._lastLoadedPfmTreeReadId = treeRead;
      this._lastLoadedPfmHasSnapshot = snap;
      this._lastLoadedPfmDisplayMode = displayMode;
      this._lastLoadedPfmStepVersion = step;
      this._lastLoadedPfmSnapshotPhase = phase;
    }
    if (changed.has("sessionKey") || changed.has("parentPath")) {
      const nextKey = this._cacheKey();
      if (changed.has("parentPath") && this._usingCommittedPath()) {
        const pathKey = parentPathKey(this.parentPath);
        if (pathKey !== this._lastParentPathKey) {
          this._lastParentPathKey = pathKey;
          if (!pathKey) {
            // Top load is triggered by runs view `navigateCommittedTop()` — avoid duplicate fetch.
          } else {
            const keys = (this.parentPath || [])
              .map((item) => String(item?.nodeKey || "").trim())
              .filter(Boolean);
            const focusKey = keys[keys.length - 1] || "";
            if (focusKey) {
              const resolved = this._resolveCommittedNodeKey(focusKey);
              this._committedSelectedNodeKey = resolved;
              const inflightCacheKey = this._committedScopeCacheKey("focus", resolved);
              const alreadyShowing =
                resolved === this._lastFetchedFocusNodeKey && Boolean(this._svgRaw);
              const inflightFocus = this._mindmapScopeInflight.get(inflightCacheKey);
              const alreadyLoading =
                this._activeFocusRequestKey === resolved
                || Boolean(inflightFocus);
              if (!alreadyShowing && !alreadyLoading) {
                void this._requestCommittedFocus(resolved);
              } else if (inflightFocus && !alreadyShowing) {
                void this._requestCommittedFocus(resolved);
              } else if (alreadyLoading) {
                this._beginScopeTransition(this._scopeTransitionMessageForNode(resolved), resolved);
              }
            }
          }
        }
      }
      if (nextKey !== this._historyReadForCacheKey) {
        this._historyReadForCacheKey = nextKey;
        this._warning = "";
        this._stopReplyWatcher();
        this._pendingReqId = "";
        this._pendingSentAtSec = 0;
        this._pendingDeadlineMs = 0;
        this._agentPayload = null;
        this._agentTitle = "";
        this._agentDescription = "";
        if (!this._committedHasData()) {
          this._svg = "";
          this._svgRaw = "";
          this._lastRenderedSignature = "";
        }
        if (nextKey) {
          if (!this._usingCommittedPath()) {
            void this._loadSavedMindmap();
            if (!this._isChildLevel()) this._resumePendingIfAny();
          }
        }
      }
    }
    if (changed.has("visitedNodeKeys") || changed.has("activePathNodeKeys")) {
      const visitedKey = stringArrayKey(this.visitedNodeKeys);
      const activeKey = stringArrayKey(this.activePathNodeKeys);
      if (
        visitedKey !== this._lastVisitedKeysKey ||
        activeKey !== this._lastActivePathKeysKey
      ) {
        this._lastVisitedKeysKey = visitedKey;
        this._lastActivePathKeysKey = activeKey;
        if (this._usingCommittedPath() && this._svgRaw) {
          this._presentCommittedSvg();
        } else {
          this._applySvgDecorations();
        }
      }
    }
    if (changed.has("nodes") && !this._isChildLevel() && !this._agentPayload?.nodes.length) {
      void this._loadSavedMindmap();
    }
  }

  protected updated(changed: Map<string, unknown>): void {
    super.updated(changed);
    if (this._isChildLevel()) return;
    const autoLoadKeys = [
      "executionId",
      "pfmBaselineExecutionId",
      "pfmTreeReadExecutionId",
      "pfmHasCommittedSnapshot",
      "pfmMapDisplayMode",
      "pfmRevision",
      "currentPfmRevision",
      "pfmHeaderLabel",
    ];
    if (autoLoadKeys.some((key) => changed.has(key))) {
      const deliveryPropChanged =
        changed.has("pfmHasCommittedSnapshot")
        || changed.has("pfmMapDisplayMode")
        || changed.has("pfmRevision")
        || changed.has("currentPfmRevision");
      if (deliveryPropChanged) {
        this._lastAutoLoadSignature = "";
      }
      this._maybeAutoLoadCommittedMindmap();
    }
  }

  private _committedHasData(): boolean {
    return this._committedStatus === "committed" && Boolean(this._svg);
  }

  private _usingCommittedPath(): boolean {
    // Run Explore always passes executionId: mindmap loads exclusively via pfm.mindmap (DB).
    return Boolean(this.executionId);
  }

  private _resolvedSnapshotPhase(): string {
    const explicit = String(this.pfmSnapshotPhase || "").trim();
    if (explicit) return explicit;
    if (this.pfmHasCommittedSnapshot !== true) return "baseline_preview";
    const st = String(this.runStatus || "").trim().toLowerCase();
    if (st === "completed" || st === "failed" || st === "cancelled" || st === "error") return "final";
    if (st === "running" || st === "pending") return "evolving";
    return "final";
  }

  private _hasCurrentRunDelivery(): boolean {
    const mode = String(this.pfmMapDisplayMode || "").trim().toLowerCase();
    if (mode === "previous") return false;
    if (this.hasDeliveredPfmMapReport === true) return true;
    if (mode === "current" && this.pfmHasCommittedSnapshot === true) return true;
    return false;
  }

  private _mindmapResponseHasDbTree(resp: {
    committed?: boolean;
    version?: number;
    mermaid?: string;
    clickableNodes?: unknown[];
    pfmNodeRowCount?: number;
    pfm_node_row_count?: number;
    dataSource?: string;
    data_source?: string;
  }): boolean {
    const rowCount = Math.max(
      0,
      Number(resp.pfmNodeRowCount ?? resp.pfm_node_row_count ?? 0),
    );
    const rawMermaid = String(resp.mermaid || "").trim();
    const dataSource = String(resp.dataSource ?? resp.data_source ?? "").trim();
    return (
      resp.committed === true
      || dataSource === "pfm_nodes"
      || rowCount > 0
      || Number(resp.version || 0) > 0
      || (Array.isArray(resp.clickableNodes) && resp.clickableNodes.length > 0)
      || (
        /^mindmap\b/i.test(rawMermaid)
        && !this._isAwaitingDbPlaceholder(rawMermaid)
        && rawMermaid.split("\n").length > 2
      )
    );
  }

  private _showingBaselinePreview(): boolean {
    return !this._hasCurrentRunDelivery();
  }

  private _isAwaitingDbPlaceholder(mermaid: string): boolean {
    return /awaiting saved db pfm map/i.test(String(mermaid || ""));
  }

  /** Prior-run execution id to read until this run commits its own pfm_tree. */
  private _baselineReadExecutionId(): string {
    const viewId = String(this.executionId || "").trim();
    if (this._hasCurrentRunDelivery() && viewId) return viewId;
    const mode = String(this.pfmMapDisplayMode || "").trim().toLowerCase();
    if (mode === "current" && viewId) return viewId;
    const treeRead = String(this.pfmTreeReadExecutionId || "").trim();
    const baseline = String(this.pfmBaselineExecutionId || "").trim();
    if (treeRead && viewId && treeRead === viewId) return viewId;
    if (mode === "previous") return treeRead || baseline;
    return treeRead || baseline;
  }

  private _committedTreeCandidateIds(executionId: string): string[] {
    const viewId = String(executionId || "").trim();
    const mode = String(this.pfmMapDisplayMode || "").trim().toLowerCase();
    const treeRead = this._baselineReadExecutionId();
    if (mode === "previous" && treeRead && treeRead !== viewId) {
      return [treeRead];
    }
    if (this._hasCurrentRunDelivery() && viewId) {
      return [viewId];
    }
    const readFallback = this._baselineReadExecutionId();
    if (readFallback && readFallback !== viewId) {
      return [readFallback];
    }
    if (this._hasCurrentRunDelivery() && viewId) {
      return [viewId];
    }
    if (readFallback) return [readFallback];
    return viewId ? [viewId] : [];
  }

  private _stableAutoLoadSignature(): string {
    const viewId = String(this.executionId || "").trim();
    const readId = this._baselineReadExecutionId() || viewId;
    return [viewId, readId, parentPathKey(this.parentPath)].join("|");
  }

  private _isTerminalRunView(): boolean {
    const st = String(this.runStatus || "").trim().toLowerCase();
    return st === "completed" || st === "failed" || st === "cancelled" || st === "error";
  }

  private _committedReadExecutionId(explicitRead = ""): string {
    const viewId = String(this.executionId || "").trim();
    if (this._hasCurrentRunDelivery() && viewId) return viewId;
    const readId = String(
      explicitRead
        || this._pfmTreeReadExecutionId
        || this.pfmTreeReadExecutionId
        || this.pfmBaselineExecutionId
        || viewId,
    ).trim();
    if (
      viewId
      && String(this.pfmMapDisplayMode || "").trim().toLowerCase() === "current"
      && readId === viewId
    ) {
      return viewId;
    }
    return readId || viewId;
  }

  /** Load scope=top (or focus) from DB when lineage points at a readable tree. */
  private _maybeAutoLoadCommittedMindmap(): void {
    if (this._isChildLevel()) return;
    const executionId = String(this.executionId || "").trim();
    if (!executionId) return;
    if (!this._lineageReadyForMindmapLoad()) return;

    const sig = this._stableAutoLoadSignature();
    if (!sig || sig === "|") return;

    const unchanged = sig === this._lastAutoLoadSignature;
    const stuckMissing =
      this._committedStatus === "missing" || this._committedStatus === "idle";
    if (this._isTerminalRunView() && this._committedHasData() && unchanged) {
      return;
    }
    if (unchanged && (this._committedHasData() || this._drillLoading)) {
      return;
    }
    if (unchanged && !stuckMissing) {
      return;
    }

    const force =
      Boolean(this._lastAutoLoadSignature) && (!unchanged || stuckMissing)
      || (stuckMissing && this._hasBaselineMindmapHint());
    const preserveView = this._committedHasData();
    void this._loadCommittedTree(force, { preserveView }).then(() => {
      if (this._committedHasData()) {
        this._lastAutoLoadSignature = sig;
      } else if (
        stuckMissing
        && this._hasBaselineMindmapHint()
        && this._committedStatus === "missing"
        && !this._error
      ) {
        // One delayed retry — lineage props often arrive after the first fetch was aborted.
        window.setTimeout(() => {
          if (this._committedHasData()) return;
          void this._loadCommittedTree(true, { preserveView: false });
        }, 400);
      }
    });
  }

  private _shouldKeepSvgDuringBaselineHandoff(): boolean {
    return this._showingBaselinePreview() || (this.pfmHasCommittedSnapshot !== true && Boolean(this._baselineReadExecutionId()));
  }

  private _pfmMindmapRevision(): number {
    return Math.max(Number(this.pfmRevision || 0), Number(this._committedTreeVersion || 0));
  }

  private _resolvedLineage() {
    const header = String(this.pfmHeaderLabel || "").trim();
    if (header && /V\s+\d+/.test(header)) {
      const hasCurrent = this._hasCurrentRunDelivery();
      return {
        hasCurrentPfmMap: hasCurrent,
        currentRunNumber: Math.max(0, Number(this.currentRunNumber || 0)),
        currentPfmRevision: Math.max(0, Number(this.currentPfmRevision || 0)),
        previousPfmRunNumber: Math.max(
          0,
          Number(this.previousPfmRunNumber || 0),
          Number(this.pfmSourceRunNumber || 0),
        ),
        previousPfmRevision: Math.max(0, Number(this.previousPfmRevision || 0)),
        lineageReady: true,
      };
    }
    return resolveClientPfmLineage(
      {
        runNumber: this.currentRunNumber,
        pfmGenerationVersion: this.pfmGenerationVersion,
        pfmHasCommittedSnapshot: this.pfmHasCommittedSnapshot,
        hasDeliveredPfmMapReport: this.hasDeliveredPfmMapReport,
        pfmMapDisplayMode: this.pfmMapDisplayMode,
        pfmRevision: this.pfmRevision,
        pfmStepVersion: this._pfmMindmapRevision(),
        pfmSourceRunNumber:
          this.pfmSourceRunNumber || this._committedSourceRunNumber,
        pfmBaselineTreeVersion:
          this.pfmBaselineTreeVersion
          || this.pfmSourceRunNumber
          || this._committedSourceRunNumber,
        pfmSnapshotPhase: this.pfmSnapshotPhase,
        currentRunNumber: this.currentRunNumber,
        currentPfmRevision: this.currentPfmRevision,
        previousPfmRunNumber: this.previousPfmRunNumber,
        previousPfmRevision: this.previousPfmRevision,
        pfmBaselineExecutionId: this.pfmBaselineExecutionId,
        pfmTreeReadExecutionId: this.pfmTreeReadExecutionId,
      },
      [] as PfmLineageRunRef[],
      {
        baselineExecutionId: this._baselineReadExecutionId(),
      },
    );
  }

  private _pfmTreeVersionLabel(): string {
    const preset = String(this.pfmHeaderLabel || "").trim();
    if (preset && /V\s+\d+/.test(preset)) return preset;
    const fromProps = formatPfmMindmapHeaderFromExecution({
      runNumber: this.currentRunNumber || this.pfmGenerationVersion,
      pfmGenerationVersion: this.pfmGenerationVersion,
      pfmHasCommittedSnapshot: this.pfmHasCommittedSnapshot,
      pfmMapDisplayMode: this.pfmMapDisplayMode,
      pfmRevision: this.pfmRevision,
      pfmStepVersion: this._pfmMindmapRevision(),
      pfmSourceRunNumber: this.pfmSourceRunNumber || this._committedSourceRunNumber,
      pfmBaselineTreeVersion:
        this.pfmBaselineTreeVersion
        || this.pfmSourceRunNumber
        || this._committedSourceRunNumber,
      pfmSnapshotPhase: this.pfmSnapshotPhase,
      currentRunNumber: this.currentRunNumber,
      currentPfmRevision: this.currentPfmRevision,
      previousPfmRunNumber: this.previousPfmRunNumber,
      previousPfmRevision: this.previousPfmRevision,
    });
    if (!/version pending/i.test(fromProps)) return fromProps;
    const lineage = this._resolvedLineage();
    return formatPfmMindmapDisplayLabel({
      currentRunNumber: lineage.currentRunNumber,
      hasCurrentPfmMap: lineage.hasCurrentPfmMap,
      currentPfmRevision: lineage.currentPfmRevision,
      previousPfmRunNumber: lineage.previousPfmRunNumber,
      previousPfmRevision: lineage.previousPfmRevision,
    });
  }

  /** True when this run view can request pfm.mindmap (DB-backed top / focus scopes). */
  private _lineageReadyForMindmapLoad(): boolean {
    if (String(this.executionId || "").trim()) return true;
    return false;
  }

  private _scopeTransitionMessageForLoad(): string {
    const header = this._pfmTreeVersionLabel();
    if (/V\s+\d+/.test(header)) {
      return `Loading ${header} from saved map…`;
    }
    return "Loading saved feature map…";
  }

  private _scopeTransitionMessageForNode(nodeKey: string, titleHint = ""): string {
    const label = stripPfmDisplayTitle(
      titleHint
        || this._committedTitleByNodeKey.get(nodeKey)
        || nodeKey.split("/").pop()
        || nodeKey
        || "selected node",
    );
    const header = this._pfmTreeVersionLabel();
    if (label && /V\s+\d+/.test(header)) {
      return `Loading feature map for “${label}” (${header})…`;
    }
    if (label) {
      return `Loading feature map for “${label}”…`;
    }
    return "Loading feature map for the selected node…";
  }

  private _knownCommittedNodeKeys(): Iterable<string> {
    return this._committedTitleByNodeKey.keys();
  }

  private _resolveCommittedNodeKey(nodeKey: string): string {
    return resolveCommittedNodeKeyInTree(nodeKey, this._knownCommittedNodeKeys());
  }

  private _syncCommittedSelectionFromParentPath(): void {
    const keys = (this.parentPath || [])
      .map((item) => this._resolveCommittedNodeKey(String(item?.nodeKey || "").trim()))
      .filter(Boolean);
    this._committedNodePath = keys;
    this._committedSelectedNodeKey = keys[keys.length - 1] || "";
  }

  private _toggleCommittedMapMaximized(): void {
    if (this.suppressInternalMaximize) return;
    this._committedMapMaximized = !this._committedMapMaximized;
  }


  render() {
    if (this._usingCommittedPath()) {
      return this._renderCommittedPath();
    }
    const hasMindmap = Boolean(this._agentPayload?.nodes.length && this._svg);
    const isPending = Boolean(this._pendingReqId);
    const isChild = this._isChildLevel();
    const generateDisabled = this._loading || isPending;
    const buttonBaseLabel = hasMindmap ? "Regenerate" : "Generate";
    const buttonBusyLabel = isPending
      ? "Waiting…"
      : hasMindmap
        ? "Regenerating..."
        : "Generating...";

    const pendingBanner = isPending
      ? html`
          <div
            style="display:flex;align-items:center;justify-content:space-between;gap:10px;margin:0 0 8px;font-size:12px;padding:8px 10px;border:1px solid color-mix(in srgb, var(--accent, #6ea8fe) 40%, transparent);border-radius:8px;background:color-mix(in srgb, var(--accent, #6ea8fe) 8%, transparent);"
          >
            <div style="min-width:0;line-height:1.45;">
              Waiting for agent reply to <strong>${this._pendingReqId}</strong>
              ${this._formatPendingRemaining()}
            </div>
            <button class="btn" type="button" @click=${() => this._cancelPending()}>
              Cancel waiting
            </button>
          </div>
        `
      : "";

    const generateButton = isChild
      ? ""
      : html`
          <button class="btn" style="margin-top:12px;" type="button" ?disabled=${generateDisabled} @click=${() => void this._regenerateMindmap()}>
            ${generateDisabled ? buttonBusyLabel : buttonBaseLabel}
          </button>
        `;

    if (this._error) {
      return html`
        <div class="state state--error">
          <div>
            ${pendingBanner}
            <div>${this._error}</div>
            ${generateButton}
          </div>
        </div>
      `;
    }
    if (!hasMindmap) {
      const root = this._mindmapRootLabel();
      const leafDesc = String(this.leafDescription || "").trim();
      const fallbackPayload = this._fallbackPayloadFromRunNodes();
      const emptyMessage = isChild
        ? ""
        : isPending
          ? "Generating PFM mindmap…"
          : "No PFM mindmap yet. Click Generate to create one from the agent.";
      return html`
        <div class="state">
          <div>
            ${pendingBanner}
            ${isChild
              ? html`
                  <div class="leaf-state">
                    <div class="leaf-title">${root}</div>
                    <div class="leaf-description">
                      ${leafDesc || "Leaf node reached. No further child PFM nodes."}
                    </div>
                  </div>
                `
              : fallbackPayload?.nodes.length
                ? this._renderFallbackNodeMap(fallbackPayload)
                : html`<div>${emptyMessage}</div>`}
            ${this._warning ? html`<div class="state--error" style="margin-top:8px;font-size:12px;">${this._warning}</div>` : ""}
            ${generateButton}
          </div>
        </div>
      `;
    }
    return html`
      <div class="canvas">
        <div class="toolbar">
          ${this._agentTitle || this._agentDescription
            ? html`
              <div class="meta-card">
                ${this._agentTitle
                  ? html`<div class="meta-title">${this._agentTitle}</div>`
                  : ""}
                ${this._agentDescription
                  ? html`<div class="meta-description">${this._agentDescription}</div>`
                  : ""}
              </div>
            `
            : html`<div></div>`}
          ${isChild
            ? ""
            : html`
                <button class="btn" type="button" ?disabled=${generateDisabled} @click=${() => void this._regenerateMindmap()}>
                  ${generateDisabled ? buttonBusyLabel : buttonBaseLabel}
                </button>
              `}
        </div>
        ${pendingBanner}
        ${this._warning
          ? html`<div class="state--error" style="margin:0 0 8px;font-size:12px;padding:8px 10px;border:1px solid color-mix(in srgb, #fca5a5 40%, transparent);border-radius:8px;background:color-mix(in srgb, #fca5a5 8%, transparent);">${this._warning}</div>`
          : ""}
        <div class="svg-wrap" @click=${this._handleSvgClick}>${unsafeHTML(this._svg)}</div>
      </div>
    `;
  }

  private _renderCommittedPath(): ReturnType<typeof html> {
    const status = this._committedStatus;
    const ver = this._committedTreeVersion;
    const useInternalMaximize = !this.suppressInternalMaximize;
    const canvasClass =
      useInternalMaximize && this._committedMapMaximized ? "canvas canvas--maximized" : "canvas";

    const controls = html`
      <div class="map-header">
        <div class="map-title-row">
          <span class="map-title">${this._pfmTreeVersionLabel()}</span>
          <span class="icon-actions">
            <button
              class="icon-btn"
              type="button"
              ?disabled=${status === "loading"}
              title="Reload saved EAD feature map from database"
              aria-label="Reload saved EAD feature map from database"
              @click=${() => void this.reloadFromDb()}
            >
              <span class=${status === "loading" ? "icon-spin" : ""}>
                ${status === "loading" ? icons.loader : icons.refresh}
              </span>
            </button>
            ${useInternalMaximize
              ? html`
                  <button
                    class="icon-btn"
                    type="button"
                    title=${this._committedMapMaximized ? "Exit maximized map" : "Maximize map"}
                    aria-label=${this._committedMapMaximized ? "Exit maximized map" : "Maximize map"}
                    @click=${() => this._toggleCommittedMapMaximized()}
                  >
                    <span>${this._committedMapMaximized ? icons.minimize : icons.maximize}</span>
                  </button>
                `
              : ""}
          </span>
        </div>
        ${this._mapLeafSubtitle
          ? html`
              <div
                class="map-leaf-subtitle ${this._mapLeafSubtitleFading ? "map-leaf-subtitle--out" : ""}"
                role="status"
                aria-live="polite"
              >
                ${this._mapLeafSubtitle}
              </div>
            `
          : ""}
      </div>
    `;

    if (status === "idle" && !this._lineageReadyForMindmapLoad()) {
      const waitingLabel = this._scopeTransitionMessageForLoad();
      return html`
        <div class=${canvasClass}>
          <div class="state">
            <div>
              ${controls}
              <div class="scope-transition" role="status">
                <span class="scope-transition__spinner icon-spin">${icons.loader}</span>
                <div>${waitingLabel}</div>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    if (this._error) {
      return html`
        <div class=${canvasClass}>
          <div class="state state--error">
            <div>
              ${controls}
              <div>${this._error}</div>
            </div>
          </div>
        </div>
      `;
    }

    if (status === "missing") {
      if (!this._usingCommittedPath()) {
        const fallback = this._fallbackPayloadFromRunNodes();
        if (fallback?.nodes.length) {
          return html`
            <div class=${canvasClass}>
              ${controls}
              ${this._renderFallbackNodeMap(fallback)}
            </div>
          `;
        }
      }
      const baselineHint = this._hasBaselineMindmapHint();
      return html`
        <div class=${canvasClass}>
          <div class="state">
            <div>
              ${controls}
              <div class="leaf-state">
                <div class="leaf-title">${baselineHint ? "Could not load saved PFM map" : "PFM mindmap not ready yet"}</div>
                <div class="leaf-description">
                  ${baselineHint
                    ? "Saved PFM node rows are in the database for the prior run, but this view could not fetch or render them. Click Reload above or hard-refresh the page."
                    : "Only a DB-saved PFM mindmap is shown here. The map appears once this run (or a prior finished run) has saved PFM node rows in the database."}
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    return html`
      <div class=${canvasClass}>
        ${controls}
        ${this._warning
          ? html`<div class="state--error" style="margin:0 0 8px;font-size:12px;padding:8px 10px;border:1px solid color-mix(in srgb,#fca5a5 40%,transparent);border-radius:8px;background:color-mix(in srgb,#fca5a5 8%,transparent);">${this._warning}</div>`
          : ""}
        <div class="mindmap-stage">
          ${this._svg
            ? html`<div
                class="svg-wrap ${this._drillLoading ? "svg-wrap--dimmed" : ""}"
                @click=${(event: MouseEvent) => void this._handleCommittedSvgClick(event)}
              >${unsafeHTML(this._svg)}</div>`
            : this._error
              ? html`<div class="state state--error">${this._error}</div>`
              : !this._drillLoading
                ? html`<div class="state">${this._scopeTransitionMessageForLoad()}</div>`
                : ""}
          ${this._drillLoading
            ? html`
                <div class="scope-transition scope-transition--overlay" role="status" aria-live="polite">
                  <span class="scope-transition__spinner icon-spin">${icons.loader}</span>
                  <div>
                    ${this._scopeTransitionMessage || this._scopeTransitionMessageForLoad()}
                  </div>
                </div>
              `
            : ""}
        </div>
        ${this._treeListPayload
          ? html`<div class="pfm-treelist-wrap">${renderPfmTreeListPanel(this._treeListPayload)}</div>`
          : ""}
      </div>
    `;
  }

  private _hasBaselineMindmapHint(): boolean {
    const readId = String(
      this.pfmTreeReadExecutionId || this.pfmBaselineExecutionId || "",
    ).trim();
    const viewId = String(this.executionId || "").trim();
    if (readId && viewId && readId !== viewId) return true;
    return (
      Math.max(0, Number(this.pfmSourceRunNumber || 0)) > 0
      || Math.max(0, Number(this.previousPfmRunNumber || 0)) > 0
      || Math.max(0, Number(this._committedSourceRunNumber || 0)) > 0
    );
  }

  private async _loadCommittedTree(
    force = false,
    opts?: { preserveView?: boolean },
  ): Promise<void> {
    // DB-only loader for Run Explore. Do not call pfm.requestSnapshot from here.
    const executionId = String(this.executionId || "").trim();
    if (!executionId) return;
    if (!this._lineageReadyForMindmapLoad()) {
      this._committedStatus = "idle";
      return;
    }
    const loadGen = ++this._committedLoadGeneration;
    const readId = this._baselineReadExecutionId() || executionId;
    const loadKey = this._stableAutoLoadSignature();
    if (!force && loadKey === this._lastAutoLoadSignature && this._committedHasData()) {
      return;
    }
    const preserveView =
      (opts?.preserveView === true || (!force && this._committedHasData()))
      && this._committedHasData();
    this._committedLoadedExecutionId = executionId;
    if (!preserveView) {
      this._committedStatus = "loading";
      this._error = "";
      this.requestUpdate();
    }
    const candidateIds = this._committedTreeCandidateIds(executionId);
    const prefetchRead = candidateIds[0] || "";
    this._pfmTreeReadExecutionId =
      prefetchRead && prefetchRead !== executionId ? prefetchRead : "";
    const focusKey =
      this._focusKeyFromParentPath() || String(this._committedSelectedNodeKey || "").trim();
    try {
      if (force && focusKey) {
        await this._requestCommittedFocus(focusKey);
      } else {
        await this._renderCommittedTop(preserveView);
      }
      if (loadGen !== this._committedLoadGeneration) return;
      if (this._svg) {
        this._committedStatus = "committed";
        if (!focusKey) {
          this._lastAutoLoadSignature = this._stableAutoLoadSignature();
        }
      } else if (!this._error) {
        this._committedStatus = "missing";
      }
      this._lastLoadedPfmBaselineId = String(this.pfmBaselineExecutionId || "").trim();
      this._lastLoadedPfmHasSnapshot = this._hasCurrentRunDelivery();
    } catch (error) {
      if (loadGen !== this._committedLoadGeneration) return;
      this._pfmTreeReadExecutionId = "";
      this._committedStatus = "missing";
      this._committedTreeVersion = 0;
      this._committedSourceRunNumber = 0;
      this._committedGeneratedAt = 0;
      this._svg = "";
      this._committedTitleByNodeKey = new Map();
      this._committedChildCountByNodeKey = new Map();
      this._centerHubKeys = new Set();
      this._error = error instanceof Error ? error.message : "Failed to load EAD feature map";
    }
  }

  private _resolveCommittedScopeCache(
    cacheKey: string,
  ): { entry: NonNullable<ReturnType<typeof getCommittedMindmapScopeCache>>; fromPersisted: boolean } | null {
    const memory = getCommittedMindmapScopeCache(cacheKey);
    if (memory) return { entry: memory, fromPersisted: false };
    const persisted = getPersistedCommittedMindmapScopeCache(cacheKey);
    if (persisted) return { entry: persisted, fromPersisted: true };
    return null;
  }

  private _committedScopeCacheKey(
    scope: "top" | "focus",
    nodeKey: string,
    readExecutionId = "",
  ): string {
    const viewId = String(this.executionId || "").trim();
    const readId = this._committedReadExecutionId(readExecutionId);
    const readingBaseline = Boolean(readId && viewId && readId !== viewId);
    const focus =
      scope === "focus" ? this._resolveCommittedNodeKey(String(nodeKey || "").trim()) : "";
    return [viewId, readId, scope, focus].join("|");
  }

  private _applyCommittedScopeCacheEntry(
    entry: ReturnType<typeof getCommittedMindmapScopeCache>,
    seq: number,
    opts?: { fromPersistedCache?: boolean },
  ): boolean {
    if (!entry || seq !== this._committedRenderSeq) return false;
    this._error = "";
    this._showingCachedMindmapView = Boolean(opts?.fromPersistedCache);
    this._applyClickableNodesFromResponse(entry.clickableNodes);
    if (entry.sourceReadId) this._pfmTreeReadExecutionId = entry.sourceReadId;
    this._committedSourceRunNumber = Math.max(0, Number(entry.sourceRunNumber || 0));
    if (entry.version) this._committedTreeVersion = entry.version;
    if (entry.generatedAt) this._committedGeneratedAt = entry.generatedAt;
    this._treeListPayload = parsePfmTreeListPayload(entry.treeListPayload ?? null);
    this._svgRaw = entry.svgRaw;
    this._presentCommittedSvg();
    this._committedStatus = "committed";
    if (this._committedScope === "focus" && this._committedSelectedNodeKey) {
      this._lastFetchedFocusNodeKey = this._resolveCommittedNodeKey(
        this._committedSelectedNodeKey,
      );
    } else if (this._committedScope === "top") {
      this._lastFetchedFocusNodeKey = "";
    }
    return true;
  }

  private _focusKeyFromParentPath(): string {
    const keys = (this.parentPath || [])
      .map((item) => String(item?.nodeKey || "").trim())
      .filter(Boolean);
    if (!keys.length) return "";
    return this._resolveCommittedNodeKey(keys[keys.length - 1] || "");
  }

  /** Single entry for drill-down loads (dedupes click + breadcrumb + parentPath updates). */
  private async _requestCommittedFocus(nodeKey: string, _preserveView = false): Promise<void> {
    const key = this._resolveCommittedNodeKey(String(nodeKey || "").trim());
    if (!key) {
      await this._renderCommittedTop();
      return;
    }
    const focusCacheKey = this._committedScopeCacheKey("focus", key);
    const cached = this._resolveCommittedScopeCache(focusCacheKey);
    if (cached) {
      const seq = ++this._committedRenderSeq;
      if (this._applyCommittedScopeCacheEntry(cached.entry, seq, { fromPersistedCache: cached.fromPersisted })) {
        this._committedSelectedNodeKey = key;
        this._lastFetchedFocusNodeKey = key;
        this._endScopeTransition();
        return;
      }
    }
    if (
      key === this._lastFetchedFocusNodeKey
      && this._committedScope === "focus"
      && this._svgRaw
      && !this._drillLoading
    ) {
      this._committedSelectedNodeKey = key;
      return;
    }
    const inflightFocus = this._mindmapScopeInflight.get(focusCacheKey);
    if (inflightFocus) {
      this._beginScopeTransition(this._scopeTransitionMessageForNode(key), key);
      await inflightFocus;
      if (key === this._lastFetchedFocusNodeKey && this._committedScope === "focus" && this._svgRaw) {
        this._committedSelectedNodeKey = key;
        this._endScopeTransition(key);
        return;
      }
    }
    if (this._drillLoading && this._activeFocusRequestKey === key) return;
    this._activeFocusRequestKey = key;
    ++this._committedRenderSeq;
    if (!this._drillLoading || this._drillTransitionNodeKey !== key) {
      this._beginScopeTransition(this._scopeTransitionMessageForNode(key), key);
    }
    try {
      await this._renderCommittedFocus(key);
    } finally {
      if (this._activeFocusRequestKey === key) this._activeFocusRequestKey = "";
      this._endScopeTransition(key);
    }
  }

  private _beginScopeTransition(message: string, nodeKey = ""): void {
    this._scopeTransitionMessage = message;
    this._drillTransitionNodeKey = String(nodeKey || "").trim();
    this._drillLoading = true;
    this.requestUpdate();
  }

  private _endScopeTransition(nodeKey = ""): void {
    const key = String(nodeKey || "").trim();
    if (key && this._drillTransitionNodeKey && key !== this._drillTransitionNodeKey) {
      return;
    }
    this._drillLoading = false;
    this._drillTransitionNodeKey = "";
    this._scopeTransitionMessage = "";
    this.requestUpdate();
  }

  /** Top map: product center + level-1 modules (gateway reads DB, scope=top only). */
  private async _renderCommittedTop(preserveView = false): Promise<void> {
    this._committedSelectedNodeKey = "";
    this._highlightedLeafNodeKey = "";
    this._dismissMapLeafSubtitle();
    this._committedNodePath = [];
    this._committedScope = "top";
    const cacheKey = this._committedScopeCacheKey("top", "");
    const cached = this._resolveCommittedScopeCache(cacheKey);
    if (cached) {
      const seq = ++this._committedRenderSeq;
      if (this._applyCommittedScopeCacheEntry(cached.entry, seq, { fromPersistedCache: cached.fromPersisted })) {
        return;
      }
    }
    if (!preserveView) {
      this._beginScopeTransition(this._scopeTransitionMessageForLoad());
    }
    try {
      await this._renderCommittedScope({ scope: "top" });
    } finally {
      if (!preserveView) {
        this._endScopeTransition();
      }
    }
  }

  /** Drill map: one module's children (gateway reads DB, scope=focus + nodeKey). */
  private async _renderCommittedFocus(nodeKey: string): Promise<void> {
    const key = this._resolveCommittedNodeKey(String(nodeKey || "").trim());
    if (!key) {
      await this._renderCommittedTop();
      return;
    }
    this._highlightedLeafNodeKey = "";
    this._dismissMapLeafSubtitle();
    this._committedSelectedNodeKey = key;
    this._committedNodePath = key.split("/").reduce<string[]>((acc, _part, idx, parts) => {
      acc.push(parts.slice(0, idx + 1).join("/"));
      return acc;
    }, []);
    this._committedScope = "focus";
    await this._renderCommittedScope({ scope: "focus", nodeKey: key });
  }

  private async _renderCommittedScope(opts?: {
    scope?: "top" | "focus";
    nodeKey?: string;
  }): Promise<void> {
    const viewExecutionId = String(this.executionId || "").trim();
    if (!viewExecutionId) return;
    const scope =
      opts?.scope === "focus" || opts?.scope === "top"
        ? opts.scope
        : String(this._committedSelectedNodeKey || "").trim()
          ? "focus"
          : "top";
    const nodeKey =
      scope === "focus"
        ? String(opts?.nodeKey || this._committedSelectedNodeKey || "").trim()
        : "";
    const cacheKey = this._committedScopeCacheKey(
      scope === "focus" ? "focus" : "top",
      nodeKey,
      String(
        this._pfmTreeReadExecutionId
          || this.pfmTreeReadExecutionId
          || this.pfmBaselineExecutionId
          || "",
      ).trim(),
    );
    const seq = ++this._committedRenderSeq;
    const cached = this._resolveCommittedScopeCache(cacheKey);
    if (cached && this._applyCommittedScopeCacheEntry(cached.entry, seq, { fromPersistedCache: cached.fromPersisted })) {
      return;
    }

    const inflight = this._mindmapScopeInflight.get(cacheKey);
    if (inflight) {
      await inflight;
      if (seq !== this._committedRenderSeq) return;
      const cachedAfter = this._resolveCommittedScopeCache(cacheKey);
      if (cachedAfter && this._applyCommittedScopeCacheEntry(cachedAfter.entry, seq, { fromPersistedCache: cachedAfter.fromPersisted })) {
        return;
      }
    }

    const task = this._fetchAndRenderCommittedScope(seq, scope, nodeKey, cacheKey, opts);
    this._mindmapScopeInflight.set(cacheKey, task);
    try {
      await task;
    } finally {
      if (this._mindmapScopeInflight.get(cacheKey) === task) {
        this._mindmapScopeInflight.delete(cacheKey);
      }
    }
  }

  private async _fetchAndRenderCommittedScope(
    seq: number,
    scope: "top" | "focus",
    nodeKey: string,
    cacheKey: string,
    _opts?: { scope?: "top" | "focus"; nodeKey?: string },
  ): Promise<void> {
    // Sole data source for Run Explore mindmap: GET pfm.mindmap (pfm_nodes promoted columns only).
    this._error = "";
    const viewExecutionId = String(this.executionId || "").trim();
    if (!viewExecutionId) return;
    const params: Record<string, unknown> = {
      executionId: viewExecutionId,
      scope,
    };
    const readExecutionId = String(
      this._pfmTreeReadExecutionId
        || this.pfmTreeReadExecutionId
        || this.pfmBaselineExecutionId
        || "",
    ).trim();
    if (readExecutionId && readExecutionId !== viewExecutionId) {
      params.readExecutionId = readExecutionId;
    }
    if (nodeKey) {
      params.nodeKey = nodeKey;
    }

    const fetchIdentity = [
      viewExecutionId,
      readExecutionId || this._committedReadExecutionId(),
      scope,
      nodeKey || "",
    ].join("|");
    const priorIdentity = this._inflightMindmapFetchIdentity;
    this._inflightMindmapFetchIdentity = fetchIdentity;
    this._inflightMindmapCacheKey = cacheKey;
    if (this._mindmapFetchAbort && priorIdentity && priorIdentity !== fetchIdentity) {
      ++this._committedLoadGeneration;
      this._mindmapFetchAbort.abort();
    }
    const abort = new AbortController();
    this._mindmapFetchAbort = abort;
    let resp: Awaited<
      ReturnType<
        typeof hermesClient.request<{
          mermaid?: string;
          version?: number;
          generatedAt?: number;
          committed?: boolean;
          readExecutionId?: string;
          sourceExecutionId?: string;
          baselineExecutionId?: string;
          sourceRunNumber?: number;
          clickableNodes?: Array<{
            nodeKey?: string;
            node_key?: string;
            title?: string;
            label?: string;
            childCount?: number;
            child_count?: number;
          }>;
          pfmNodeRowCount?: number;
          pfm_node_row_count?: number;
          baselinePfmNodeRowCount?: number;
          baseline_pfm_node_row_count?: number;
          partialDeliveryWarning?: boolean;
          partial_delivery_warning?: boolean;
        }>
      >
    >;
    try {
      const requestMindmap = () =>
        hermesClient.request<{
          mermaid?: string;
          version?: number;
          generatedAt?: number;
          committed?: boolean;
          readExecutionId?: string;
          sourceExecutionId?: string;
          baselineExecutionId?: string;
          sourceRunNumber?: number;
          clickableNodes?: Array<{
            nodeKey?: string;
            node_key?: string;
            title?: string;
            label?: string;
            childCount?: number;
            child_count?: number;
          }>;
          pfmNodeRowCount?: number;
          pfm_node_row_count?: number;
          baselinePfmNodeRowCount?: number;
          baseline_pfm_node_row_count?: number;
          partialDeliveryWarning?: boolean;
          partial_delivery_warning?: boolean;
        }>("pfm.mindmap", { ...params, abortSignal: abort.signal });
      const retryDelayMs = [350, 800];
      resp = await requestMindmap();
      if (!resp && !abort.signal.aborted) {
        const firstErr = String(hermesClient.getLastRequestError() || "").toLowerCase();
        const isTransientGateway =
          firstErr.startsWith("502")
          || firstErr.startsWith("503")
          || firstErr.startsWith("504")
          || firstErr.includes("bad gateway")
          || firstErr.includes("gateway timeout");
        if (isTransientGateway) {
          for (const waitMs of retryDelayMs) {
            await new Promise<void>((resolve) => {
              window.setTimeout(() => resolve(), waitMs);
            });
            if (abort.signal.aborted) break;
            resp = await requestMindmap();
            if (resp) break;
            const retryErr = String(hermesClient.getLastRequestError() || "").toLowerCase();
            if (
              !(
                retryErr.startsWith("502")
                || retryErr.startsWith("503")
                || retryErr.startsWith("504")
                || retryErr.includes("bad gateway")
                || retryErr.includes("gateway timeout")
              )
            ) {
              break;
            }
          }
        }
      }
    } finally {
      if (this._inflightMindmapCacheKey === cacheKey) {
        this._inflightMindmapCacheKey = "";
      }
      if (this._inflightMindmapFetchIdentity === fetchIdentity) {
        this._inflightMindmapFetchIdentity = "";
      }
      if (this._mindmapFetchAbort === abort) this._mindmapFetchAbort = null;
    }
    if (seq !== this._committedRenderSeq) return;
    const resolvedFocus =
      scope === "focus" && nodeKey
        ? this._resolveCommittedNodeKey(nodeKey)
        : "";
    if (!resp) {
      if (abort.signal.aborted) return;
      const detail = hermesClient.getLastRequestError();
      this._error = detail
        ? `Mindmap data unavailable from gateway: ${detail}`
        : "Mindmap data unavailable from gateway.";
      return;
    }
    const sourceReadId = String(
      resp.sourceExecutionId || resp.readExecutionId || this._pfmTreeReadExecutionId || viewExecutionId,
    ).trim();
    if (sourceReadId) this._pfmTreeReadExecutionId = sourceReadId;
    this._committedSourceRunNumber = Math.max(0, Number(resp.sourceRunNumber || 0));
    if (resp.version) this._committedTreeVersion = resp.version;
    if (resp.generatedAt) this._committedGeneratedAt = resp.generatedAt;
    this._applyClickableNodesFromResponse(resp.clickableNodes);
    const rawMermaid = String(resp.mermaid || "").trim();
    const dataSource = String(
      (resp as { dataSource?: string; data_source?: string }).dataSource
        || (resp as { data_source?: string }).data_source
        || "",
    ).trim();
    const hasCommittedTree = this._mindmapResponseHasDbTree({
      ...resp,
      dataSource,
    });
    if (!rawMermaid || this._isAwaitingDbPlaceholder(rawMermaid) || !hasCommittedTree) {
      this._svg = "";
      if (!this._error) {
        this._error = hasCommittedTree
          ? "Mindmap data from gateway was empty."
          : "No saved PFM node rows were returned for this scope.";
      }
      return;
    }
    if (scope === "focus" && nodeKey) {
      const focusKey = this._resolveCommittedNodeKey(nodeKey);
      if (focusKey) {
        const childCount = (resp.clickableNodes || []).length;
        this._committedChildCountByNodeKey.set(focusKey, childCount);
        const rootMatch = rawMermaid.match(/^\s*root\(\((.+)\)\)\s*$/m);
        const rootTitle = rootMatch?.[1]
          ? stripPfmDisplayTitle(rootMatch[1].replace(/\[/g, "(").replace(/\]/g, ")"))
          : "";
        if (rootTitle) this._committedTitleByNodeKey.set(focusKey, rootTitle);
      }
    }
    const mermaidText = this._withSelectedNodeAsRoot(this._stripRunPrefixFromRoot(rawMermaid));
    this._activeRenderCacheKey = cacheKey;
    await this._renderMermaidText(mermaidText, seq, cacheKey);
    this._activeRenderCacheKey = "";
    if (seq !== this._committedRenderSeq) return;
    const partialWarning = Boolean(
      (resp as { partialDeliveryWarning?: boolean; partial_delivery_warning?: boolean })
        .partialDeliveryWarning
      ?? (resp as { partial_delivery_warning?: boolean }).partial_delivery_warning,
    );
    const currentCount = Math.max(
      0,
      Number(
        (resp as { pfmNodeRowCount?: number; pfm_node_row_count?: number }).pfmNodeRowCount
          ?? (resp as { pfm_node_row_count?: number }).pfm_node_row_count
          ?? 0,
      ),
    );
    const baselineCount = Math.max(
      0,
      Number(
        (resp as { baselinePfmNodeRowCount?: number; baseline_pfm_node_row_count?: number })
          .baselinePfmNodeRowCount
          ?? (resp as { baseline_pfm_node_row_count?: number }).baseline_pfm_node_row_count
          ?? 0,
      ),
    );
    if (partialWarning && currentCount > 0 && baselineCount > currentCount) {
      this._warning =
        `Current delivery has ${currentCount} nodes; inherited baseline had ${baselineCount}. `
        + "Partial delivery detected — next import should preserve inherited keys.";
    } else {
      this._warning = "";
    }
    this._treeListPayload = parsePfmTreeListPayload(
      (resp as { treeList?: unknown; tree_list?: unknown }).treeList
        ?? (resp as { tree_list?: unknown }).tree_list,
    );
    if (this._svg) {
      this._committedStatus = "committed";
      if (resolvedFocus) this._lastFetchedFocusNodeKey = resolvedFocus;
      if (scope === "top") this._lastFetchedFocusNodeKey = "";
      this._showingCachedMindmapView = false;
      setCommittedMindmapScopeCache(cacheKey, {
        mermaid: mermaidText,
        svg: this._svg,
        svgRaw: this._svgRaw,
        clickableNodes: resp.clickableNodes || [],
        treeListPayload:
          (resp as { treeList?: unknown; tree_list?: unknown }).treeList
          ?? (resp as { tree_list?: unknown }).tree_list
          ?? null,
        version: Number(resp.version || 0),
        sourceReadId: sourceReadId,
        sourceRunNumber: Math.max(0, Number(resp.sourceRunNumber || 0)),
        generatedAt: Number(resp.generatedAt || 0),
      });
    } else if (!this._error) {
      this._error = "Display renderer failed for the saved PFM map.";
    }
  }

  private async _renderMermaidText(
    definition: string,
    expectedSeq = 0,
    cacheKey = "",
  ): Promise<void> {
    const trimmed = this._sanitizeMermaidDefinition(String(definition || "")).trim();
    if (!trimmed) {
      if (expectedSeq && expectedSeq !== this._committedRenderSeq) return;
      this._svg = "";
      this._svgRaw = "";
      this._committedMermaidRenderSignature = "";
      return;
    }
    if (
      trimmed === this._committedMermaidRenderSignature
      && this._svgRaw
      && (!expectedSeq || expectedSeq === this._committedRenderSeq)
    ) {
      this._presentCommittedSvg();
      return;
    }
    try {
      const svg = await renderMermaidToSvg(trimmed, "pfm-tree-map");
      if (expectedSeq && expectedSeq !== this._committedRenderSeq) return;
      this._svgRaw = svg || "";
      this._committedMermaidRenderSignature = trimmed;
      this._showingCachedMindmapView = false;
      this._presentCommittedSvg();
    } catch (error) {
      if (expectedSeq && expectedSeq !== this._committedRenderSeq) return;
      const fallbackKey = String(cacheKey || this._activeRenderCacheKey || "").trim();
      const fallback = fallbackKey ? this._resolveCommittedScopeCache(fallbackKey) : null;
      if (fallback?.entry?.svgRaw) {
        this._svgRaw = fallback.entry.svgRaw;
        this._committedMermaidRenderSignature = String(fallback.entry.mermaid || "").trim();
        this._showingCachedMindmapView = true;
        this._error = "";
        this._warning =
          "Display renderer failed; showing last saved DB view from this session.";
        this._presentCommittedSvg();
        return;
      }
      this._svg = "";
      this._svgRaw = "";
      this._error =
        error instanceof Error
          ? `Display renderer failed: ${error.message}`
          : "Display renderer failed for committed mindmap.";
    }
  }

  private async _jumpToCommittedAncestor(nodeKey: string, _depthHint?: number): Promise<void> {
    const key = String(nodeKey || "").trim();
    this._warning = "";
    if (!key) {
      await this._renderCommittedTop(this._committedHasData());
    } else {
      await this._requestCommittedFocus(key);
    }
    this._scheduleViewStatePut();
    this._emitCommittedSelectionChange();
  }

  private async _handleCommittedSvgClick(event: MouseEvent): Promise<void> {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const tagged = target.closest("[data-pfm-node-key]");
    let matched = "";
    if (tagged instanceof Element) {
      matched = String(tagged.getAttribute("data-pfm-node-key") || "").trim();
    }
    if (!matched) {
      const group = target.closest("g");
      const rawText = String(group?.textContent || target.textContent || "")
        .replace(/\s+/g, " ")
        .trim();
      if (!rawText) return;
      const labelOnly = rawText.replace(/\s+\[(Success|Failed|No Run)\]\s*$/i, "").trim();
      matched = this._findCommittedNodeByLabel(labelOnly);
    }
    if (!matched) return;
    const childCount = Number(this._committedChildCountByNodeKey.get(matched) || 0);
    this._committedSelectedNodeKey = matched;
    const parentKeys = (this.parentPath || [])
      .map((item) => String(item?.nodeKey || "").trim())
      .filter(Boolean);
    // In some gateway payloads, top-scope clickable nodes can miss child counts (reported as 0).
    // When clicking from top scope, probe once by loading focus before declaring leaf.
    const shouldProbeFocusFromTop = this._committedScope === "top";
    const drillDown = childCount > 0 || shouldProbeFocusFromTop;
    const resolvedMatch = this._resolveCommittedNodeKey(matched);
    const nodePath = drillDown
      ? appendDrillPathKey(parentKeys, resolvedMatch)
      : parentKeys;
    this._emitCommittedSelectionChange({ nodePath, drillDown });
    if (drillDown) {
      this._highlightedLeafNodeKey = "";
      this._dismissMapLeafSubtitle();
      this._committedSelectedNodeKey = resolvedMatch;
      if (!this._drillLoading || this._drillTransitionNodeKey !== resolvedMatch) {
        this._beginScopeTransition(
          this._scopeTransitionMessageForNode(resolvedMatch),
          resolvedMatch,
        );
      }
      void this._requestCommittedFocus(resolvedMatch);
      this._scheduleViewStatePut();
    } else {
      this._highlightedLeafNodeKey = resolvedMatch;
      this._showMapLeafSubtitle("Leaf node reached. No further child PFM nodes.");
      this._presentCommittedSvg();
    }
  }

  private _clearMapLeafSubtitleTimers(): void {
    if (this._mapLeafSubtitleTimer != null) {
      clearTimeout(this._mapLeafSubtitleTimer);
      this._mapLeafSubtitleTimer = null;
    }
    if (this._mapLeafSubtitleFadeTimer != null) {
      clearTimeout(this._mapLeafSubtitleFadeTimer);
      this._mapLeafSubtitleFadeTimer = null;
    }
  }

  private _dismissMapLeafSubtitle(): void {
    this._clearMapLeafSubtitleTimers();
    this._mapLeafSubtitle = "";
    this._mapLeafSubtitleFading = false;
  }

  private _showMapLeafSubtitle(message: string, visibleMs = 2800): void {
    this._clearMapLeafSubtitleTimers();
    this._mapLeafSubtitleFading = false;
    this._mapLeafSubtitle = String(message || "").trim();
    const fadeLeadMs = 450;
    this._mapLeafSubtitleFadeTimer = setTimeout(() => {
      this._mapLeafSubtitleFading = true;
    }, Math.max(0, visibleMs - fadeLeadMs));
    this._mapLeafSubtitleTimer = setTimeout(() => {
      this._dismissMapLeafSubtitle();
    }, visibleMs);
  }

  private _presentCommittedSvg(): void {
    if (!this._svgRaw) {
      this._svg = "";
      return;
    }
    const fitted = this._fitSvgToPanel(this._svgRaw);
    const clickable = this._decorateCommittedSvgForClicks(fitted);
    this._svg = this._applyCommittedSvgHighlights(clickable);
  }

  private _emitCommittedSelectionChange(opts?: {
    nodePath?: string[];
    drillDown?: boolean;
  }): void {
    const nodeKey = this._committedSelectedNodeKey;
    if (!nodeKey) return;
    const rawTitle =
      this._committedTitleByNodeKey.get(nodeKey) || nodeKey.split("/").pop() || nodeKey;
    const nodePath =
      Array.isArray(opts?.nodePath) && opts.nodePath.length
        ? opts.nodePath
        : [...this._committedNodePath];
    this.dispatchEvent(
      new CustomEvent("pfm-node-selected", {
        detail: {
          nodeKey,
          title: stripPfmDisplayTitle(rawTitle),
          nodePath,
          index: 0,
          source: "committed-tree",
          drillDown: opts?.drillDown === true,
          pfmTreeVersion: this._committedTreeVersion,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _findCommittedNodeByLabel(label: string): string {
    const normalized = stripPfmDisplayTitle(label).replace(/\s+/g, " ").trim().toLowerCase();
    if (!normalized) return "";
    const cached = this._committedLabelIndex;
    if (!cached) return "";
    if (cached.has(normalized)) return cached.get(normalized) || "";
    for (const [labelKey, nodeKey] of cached.entries()) {
      if (normalized.startsWith(labelKey + " ") || labelKey.startsWith(normalized + " ")) {
        return nodeKey;
      }
    }
    return "";
  }

  private _committedLabelIndex: Map<string, string> | null = null;
  private _committedTitleByNodeKey: Map<string, string> = new Map();
  private _committedChildCountByNodeKey: Map<string, number> = new Map();
  private _centerHubKeys: Set<string> = new Set();

  private _applyClickableNodesFromResponse(
    nodes?: Array<{
      nodeKey?: string;
      node_key?: string;
      title?: string;
      label?: string;
      childCount?: number;
      child_count?: number;
    }>,
  ): void {
    const labelIndex = new Map<string, string>();
    const titleByKey = new Map<string, string>();
    const childCountByKey = new Map<string, number>();
    for (const node of nodes || []) {
      const nk = String(node.nodeKey || node.node_key || "").trim();
      if (!nk) continue;
      const title = String(node.title || "").trim();
      const label = String(node.label || title || nk).trim();
      if (title) titleByKey.set(nk, title);
      childCountByKey.set(nk, Math.max(0, Number(node.childCount ?? node.child_count ?? 0)));
      for (const candidate of [label, title]) {
        const normalized = stripPfmDisplayTitle(candidate)
          .replace(/\s+\[(Success|Failed|No Run)\]\s*$/i, "")
          .replace(/\s+/g, " ")
          .trim()
          .toLowerCase();
        if (normalized && !labelIndex.has(normalized)) {
          labelIndex.set(normalized, nk);
        }
      }
    }
    this._committedLabelIndex = labelIndex.size ? labelIndex : null;
    this._committedTitleByNodeKey = titleByKey;
    this._committedChildCountByNodeKey = childCountByKey;
  }

  private _decorateCommittedSvgForClicks(svg: string): string {
    const index = this._committedLabelIndex;
    if (!svg || !index?.size) return svg;
    try {
      const doc = new DOMParser().parseFromString(svg, "image/svg+xml");
      const groups = Array.from(doc.querySelectorAll("g")).filter((g) =>
        Boolean(g.querySelector("text, tspan")),
      );
      const depthOf = (el: Element): number => {
        let depth = 0;
        let cur: Element | null = el;
        while (cur?.parentElement) {
          depth += 1;
          cur = cur.parentElement;
        }
        return depth;
      };
      groups.sort((a, b) => depthOf(b) - depthOf(a));
      for (const group of groups) {
        if (group.closest("[data-pfm-node-key]")) continue;
        const rawText = String(group.textContent || "")
          .replace(/\s+/g, " ")
          .trim();
        if (!rawText) continue;
        const labelOnly = rawText.replace(/\s+\[(Success|Failed|No Run)\]\s*$/i, "").trim();
        const nodeKey = this._findCommittedNodeByLabel(labelOnly);
        if (!nodeKey) continue;
        group.setAttribute("data-pfm-node-key", nodeKey);
        group.classList.add("pfm-drill-target");
      }
      const root = doc.documentElement;
      if (!root || root.tagName.toLowerCase() !== "svg") return svg;
      return new XMLSerializer().serializeToString(root);
    } catch {
      return svg;
    }
  }

  private _buildCommittedLabelIndex(
    flat?: Array<{ node_key?: string; title?: string; status?: string }>,
  ): Map<string, string> {
    const out = new Map<string, string>();
    for (const node of flat || []) {
      const nk = String(node?.node_key || "").trim();
      const title = String(node?.title || "")
        .replace(/<br\s*\/?>/gi, " ")
        .replace(/^(?:\d+\.)+\s*/, "")
        .replace(/^\d+\s+/, "")
        .replace(/\s+/g, " ")
        .trim();
      if (!nk || !title) continue;
      let truncated = title.replace(/[()]/g, "[").replace(/"/g, "'");
      if (truncated.length > 92) truncated = truncated.slice(0, 89) + "...";
      const status = String(node?.status || "No Run").trim();
      const labelWithStatus = `${truncated} [${status}]`.replace(/\s+/g, " ").toLowerCase().trim();
      const labelOnly = truncated.replace(/\s+/g, " ").toLowerCase().trim();
      if (!out.has(labelWithStatus)) out.set(labelWithStatus, nk);
      if (!out.has(labelOnly)) out.set(labelOnly, nk);
    }
    return out;
  }

  private _buildCommittedTitleIndex(
    flat?: Array<{ node_key?: string; title?: string }>,
  ): Map<string, string> {
    const out = new Map<string, string>();
    for (const node of flat || []) {
      const nk = String(node?.node_key || "").trim();
      const title = String(node?.title || "")
        .replace(/<br\s*\/?>/gi, " ")
        .replace(/^(?:\d+\.)+\s*/, "")
        .replace(/^\d+\s+/, "")
        .replace(/\s+/g, " ")
        .trim();
      if (!nk || !title) continue;
      out.set(nk, title);
    }
    return out;
  }

  private _buildCommittedChildCountIndex(
    flat?: Array<{ node_key?: string; parent_node_key?: string | null }>,
  ): Map<string, number> {
    const out = new Map<string, number>();
    for (const node of flat || []) {
      const parent = String(node?.parent_node_key || "").trim();
      if (!parent) continue;
      out.set(parent, Number(out.get(parent) || 0) + 1);
    }
    return out;
  }

  private _applyCommittedSvgHighlights(svg: string): string {
    if (!svg) return svg;
    try {
      const doc = new DOMParser().parseFromString(svg, "image/svg+xml");
      const root = doc.documentElement;
      if (!root || root.tagName.toLowerCase() !== "svg") return svg;
      const leafKey = this._resolveCommittedNodeKey(
        String(this._highlightedLeafNodeKey || "").trim(),
      );
      const visitedSet = new Set(
        (this.visitedNodeKeys || [])
          .map((nodeKey) => this._resolveCommittedNodeKey(String(nodeKey || "").trim()))
          .filter(Boolean),
      );
      const activeSet = new Set(
        (this.activePathNodeKeys || [])
          .map((nodeKey) => this._resolveCommittedNodeKey(String(nodeKey || "").trim()))
          .filter(Boolean),
      );
      for (const textEl of Array.from(root.querySelectorAll("text, tspan"))) {
        textEl.classList.remove("pfm-leaf-text--selected");
      }
      for (const group of Array.from(root.querySelectorAll("[data-pfm-node-key]"))) {
        const rawKey = String(group.getAttribute("data-pfm-node-key") || "").trim();
        const nodeKey = this._resolveCommittedNodeKey(rawKey);
        if (!nodeKey) continue;
        group.classList.remove("node--active-path", "node--visited-dim");
        if (leafKey && nodeKey === leafKey) {
          for (const textEl of Array.from(group.querySelectorAll("text, tspan"))) {
            textEl.classList.add("pfm-leaf-text--selected");
          }
        }
        if (activeSet.has(nodeKey)) {
          group.classList.add("node--active-path");
        }
        if (visitedSet.has(nodeKey)) {
          group.classList.add("node--visited-dim");
        }
      }
      return new XMLSerializer().serializeToString(root);
    } catch {
      return svg;
    }
  }

  private _sanitizeMindmapLabel(value: string): string {
    let raw = String(value || "").replace(/\r?\n+/g, " ").trim();
    raw = raw.replace(/#{1,6}\s*/g, "").replace(/\*\*([^*]+)\*\*/g, "$1");
    raw = raw.replace(/`([^`]+)`/g, "$1");
    // Mermaid mindmap labels can misparse raw square brackets as syntax.
    raw = raw.replaceAll("[", " ").replaceAll("]", " ");
    raw = raw.replace(/[\u2010-\u2015\u2212\u2500-\u2503-]{3,}/g, " ");
    raw = raw.replace(/-{3,}/g, " ").replace(/_{3,}/g, " ").replace(/={3,}/g, " ");
    raw = raw.replace(/\s{2,}/g, " ").trim();
    if (!raw || /^[-_.= ]+$/.test(raw)) return "";
    if (raw.length > 72) raw = `${raw.slice(0, 69)}...`;
    return raw
      .replaceAll("(", " ")
      .replaceAll(")", " ")
      .replaceAll('"', "'")
      .replace(/\s{2,}/g, " ")
      .trim();
  }

  /** Drop markdown HR lines and inline dash runs that break Mermaid mindmap parsing. */
  private _sanitizeMermaidDefinition(definition: string): string {
    return String(definition || "")
      .split(/\r?\n/)
      .map((line) => {
        const stripped = line.trim();
        if (/^[-_.=\u2010-\u2015]{3,}$/.test(stripped)) return "";
        let cleaned = line.replace(/<br\s*\/?>/gi, " ");
        cleaned = cleaned.replace(/[\u2010-\u2015\u2212\u2500-\u2503-]{3,}/g, " ");
        cleaned = cleaned.replaceAll("[", " ").replaceAll("]", " ");
        cleaned = cleaned.replace(/_{3,}/g, " ").replace(/={3,}/g, " ");
        const lead = cleaned.match(/^\s*/)?.[0] ?? "";
        const body = cleaned.slice(lead.length).replace(/\s{2,}/g, " ");
        return `${lead}${body}`;
      })
      .filter((line) => line.trim().length > 0)
      .join("\n");
  }

  private _formatCenterNodeLabel(value: string): string {
    const MAX_TOTAL = 35;
    const LINE1_MAX = 17;
    const LINE2_MAX = 18;
    let clean = this._sanitizeMindmapLabel(value);
    if (!clean) return "";
    if (clean.length > MAX_TOTAL) {
      clean = `${clean.slice(0, MAX_TOTAL - 3).trimEnd()}...`;
    }
    if (clean.length <= LINE1_MAX) return clean;
    let splitAt = clean.lastIndexOf(" ", LINE1_MAX);
    if (splitAt < 10) splitAt = LINE1_MAX;
    const line1 = clean.slice(0, splitAt).trim();
    let line2 = clean.slice(splitAt).trim();
    if (!line1 || !line2) return clean;
    if (line2.length > LINE2_MAX) {
      line2 = `${line2.slice(0, LINE2_MAX - 3).trimEnd()}...`;
    }
    // Mermaid mindmap root labels must be plain text (no HTML <br/>).
    return `${line1} ${line2}`;
  }

  private _withSelectedNodeAsRoot(definition: string): string {
    const selected = this._resolveCommittedNodeKey(
      String(this._committedSelectedNodeKey || "").trim(),
    );
    if (!selected) return definition;
    const title = this._formatCenterNodeLabel(
      this._committedTitleByNodeKey.get(selected)
        || selected.split("/").pop()
        || selected,
    );
    if (!title) return definition;
    const lines = String(definition || "").split(/\r?\n/);
    const rootIdx = lines.findIndex((line) => /^\s*root\(\(.*\)\)\s*$/.test(line));
    if (rootIdx < 0) return definition;
    lines[rootIdx] = "  root((" + title + "))";

    // Backend focus output can still include "<selected>" as a direct child under root.
    // Rule: once selected node is promoted to root, any immediate child matching the
    // selected label (after normalization) is treated as duplicate and collapsed.
    const normalizedTitle = this._normalizeLabel(title);
    let selectedLineIdx = -1;
    let selectedIndent = 0;
    for (let i = rootIdx + 1; i < lines.length; i += 1) {
      const raw = lines[i] || "";
      if (!raw.trim()) continue;
      const indent = (raw.match(/^(\s*)/)?.[1]?.length ?? 0);
      const text = this._normalizeLabel(raw.trim());
      if (text === normalizedTitle) {
        selectedLineIdx = i;
        selectedIndent = indent;
        break;
      }
      if (indent <= 2) break;
    }
    if (selectedLineIdx > -1) {
      const out = lines.slice(0, selectedLineIdx);
      for (let i = selectedLineIdx + 1; i < lines.length; i += 1) {
        const raw = lines[i] || "";
        if (!raw.trim()) {
          out.push(raw);
          continue;
        }
        const indent = (raw.match(/^(\s*)/)?.[1]?.length ?? 0);
        if (indent <= selectedIndent) {
          out.push(...lines.slice(i));
          return out.join("\n");
        }
        out.push(raw.slice(2));
      }
      return out.join("\n");
    }
    return lines.join("\n");
  }

  private _stripRunPrefixFromRoot(definition: string): string {
    if (String(this._committedSelectedNodeKey || "").trim()) return definition;
    const runLabel = String(this.rootLabel || "")
      .replace(/^Run:\s*/i, "")
      .trim();
    if (runLabel) {
      return String(definition || "").replace(
        /^\s*root\(\((.*)\)\)\s*$/m,
        () => `  root((${runLabel.replace(/[()[\]]/g, " ").replace(/"/g, "'").replace(/\s{2,}/g, " ").trim()}))`,
      );
    }
    return String(definition || "").replace(
      /^\s*root\(\(\s*Run:\s*(.*?)\s*\)\)\s*$/m,
      (_m, label: string) => `  root((${String(label || "").trim()}))`,
    );
  }

  private _scheduleViewStatePut(): void {
    if (this._viewStateSaveTimer != null) {
      clearTimeout(this._viewStateSaveTimer);
    }
    this._viewStateSaveTimer = setTimeout(() => {
      void this._putViewState();
    }, 600);
  }

  private async _putViewState(): Promise<void> {
    const runId = String(this.executionId || "").trim();
    const readId = String(this._pfmTreeReadExecutionId || "").trim();
    if (!runId) return;
    if (readId && readId !== runId) {
      return;
    }
    await hermesClient.request("pfm.viewPut", {
      executionId: runId,
      nodePath: this._committedNodePath,
      selectedNodeKey: this._committedSelectedNodeKey || null,
      viewScope: "focus",
      depthCap: 2,
      pfmTreeVersion: this._committedTreeVersion,
    });
  }

  private _formatPendingRemaining(): string {
    if (!this._pendingDeadlineMs) return "";
    const remainingMs = Math.max(0, this._pendingDeadlineMs - (this._pendingTickMs || Date.now()));
    const totalSec = Math.ceil(remainingMs / 1000);
    if (totalSec <= 0) return " (timing out…)";
    const mins = Math.floor(totalSec / 60);
    const secs = totalSec % 60;
    if (mins > 0) return ` — up to ${mins}m ${secs.toString().padStart(2, "0")}s remaining`;
    return ` — up to ${secs}s remaining`;
  }

  private _toPayload(): MermaidMapPayload {
    // Mindmap source of truth: the latest explicit node list returned by the agent command.
    if (this._agentPayload?.nodes.length) {
      return {
        map: { name: "PFM Mindmap" },
        nodes: this._agentPayload.nodes.map((node, idx) => ({
          node_id: idx + 1,
          parent_node_id: null,
          node_key: node.key,
          title: this._trimWords(node.title, 8),
        })),
        features: [],
        testCases: [],
        testSteps: [],
      };
    }
    return {
      map: { name: "PFM Mindmap" },
      nodes: [],
      features: [],
      testCases: [],
      testSteps: [],
    };
  }

  private _trimWords(value: string, limit: number): string {
    const words = String(value || "").trim().split(/\s+/).filter(Boolean);
    if (words.length <= limit) return words.join(" ");
    return `${words.slice(0, limit).join(" ")}…`;
  }

  private _extractAgentPayload(content: string): ParsedPfmNodeList {
    return parsePfmNodeListResponse(content);
  }

  private _fallbackPayloadFromRunNodes(): ParsedPfmNodeList | null {
    if (this._isChildLevel() || !Array.isArray(this.nodes) || !this.nodes.length) return null;
    const nodes = this.nodes
      .map((node, idx) => {
        const key = String(node.nodeKey || node.nodeId || "").trim();
        const title = String(node.title || node.meta || key || `PFM node ${idx + 1}`)
          .replace(/\s+/g, " ")
          .trim();
        if (!key && !title) return null;
        return {
          index: idx + 1,
          key: key || `node-${idx + 1}`,
          title,
        };
      })
      .filter((node): node is ParsedPfmNodeList["nodes"][number] => Boolean(node));
    if (!nodes.length) return null;
    return {
      title: this.rootLabel || "PFM Mindmap",
      description: "Loaded from persisted run PFM nodes.",
      nodes,
    };
  }

  private _renderFallbackNodeMap(payload: ParsedPfmNodeList): ReturnType<typeof html> {
    return html`
      <div class="fallback-map">
        <div class="fallback-title">${payload.title || "Persisted PFM Mindmap"}</div>
        <ul class="fallback-list">
          ${payload.nodes.slice(0, 40).map((node) => html`
            <li class="fallback-node">
              <strong>${node.index}.</strong> ${this._trimWords(node.title || node.key, 16)}
            </li>
          `)}
        </ul>
        ${payload.nodes.length > 40
          ? html`<div style="margin-top:8px;font-size:11px;color:var(--muted);">
              Showing first 40 of ${payload.nodes.length} persisted PFM nodes.
            </div>`
          : ""}
      </div>
    `;
  }

  private async _readLatestMessages(
    sessionKey: string,
  ): Promise<Array<{ role?: string; content?: string; timestamp?: number }>> {
    const pageSize = 80;
    const firstPage = await hermesClient.request<{
      messages?: Array<{ role?: string; content?: string; timestamp?: number }>;
      total?: number;
    }>(
      "chat.history",
      { sessionKey, limit: 1, offset: 0 },
    );
    const total = Number(firstPage?.total || firstPage?.messages?.length || 0);
    const offset = Math.max(0, total - pageSize);
    const result = await hermesClient.request<{
      messages?: Array<{ role?: string; content?: string; timestamp?: number }>;
    }>(
      "chat.history",
      { sessionKey, limit: pageSize, offset },
    );
    return result?.messages ?? [];
  }

  /** Return to project center with hub children (top-level modules). */
  navigateCommittedTop(): void {
    const executionId = String(this.executionId || "").trim();
    this._warning = "";
    this._committedSelectedNodeKey = "";
    this._highlightedLeafNodeKey = "";
    this._committedNodePath = [];
    this._committedScope = "top";
    this._lastFetchedFocusNodeKey = "";
    this._activeFocusRequestKey = "";
    this._dismissMapLeafSubtitle();
    this._mindmapScopeInflight.clear();
    this._inflightMindmapCacheKey = "";
    ++this._committedLoadGeneration;
    if (this._mindmapFetchAbort) {
      this._mindmapFetchAbort.abort();
      this._mindmapFetchAbort = null;
    }
    void this._loadCommittedTree(true, { preserveView: this._committedHasData() }).then(() => {
      this._scheduleViewStatePut();
    });
  }

  /** Focus the committed tree on a breadcrumb ancestor (module or deeper). */
  navigateCommittedFocus(nodeKey: string): void {
    this._warning = "";
    void this._requestCommittedFocus(nodeKey);
    this._scheduleViewStatePut();
  }

  /**
   * Background sync after a new DB delivery (revision bump). Keeps the current SVG visible
   * while fetching the latest scope from pfm.mindmap — no full cache wipe or loading flash.
   */
  async syncFromDb(): Promise<void> {
    const executionId = String(this.executionId || "").trim();
    if (!executionId || !this._lineageReadyForMindmapLoad() || !canPollGateway()) return;
    if (this._syncFromDbTimer != null) {
      clearTimeout(this._syncFromDbTimer);
    }
    this._syncFromDbTimer = setTimeout(() => {
      this._syncFromDbTimer = null;
      void runGatewayPoll(() =>
        this._loadCommittedTree(true, { preserveView: this._committedHasData() }),
      );
    }, 350);
  }

  /**
   * Reload the committed mindmap from the database (`pfm.mindmap`).
   *
   * Run Explore: the ONLY allowed refresh entry point. Do not add requestSnapshot,
   * delivery import, or agent regenerate here or in callers after node-report events.
   */
  async reloadFromDb(): Promise<void> {
    const executionId = String(this.executionId || "").trim();
    if (!executionId) return;
    if (!this._lineageReadyForMindmapLoad()) return;
    clearCommittedMindmapScopeCacheForExecution(executionId);
    this._mindmapScopeInflight.clear();
    this._inflightMindmapCacheKey = "";
    ++this._committedLoadGeneration;
    this._lastAutoLoadSignature = "";
    if (this._mindmapFetchAbort) {
      this._mindmapFetchAbort.abort();
      this._mindmapFetchAbort = null;
    }
    await this._loadCommittedTree(true, { preserveView: false });
  }

  /** LEGACY chat-only (no executionId). Run Explore must never reach this. */
  private async _loadSavedMindmap(): Promise<void> {
    if (this._usingCommittedPath()) return;
    const cacheKey = this._cacheKey();
    if (!cacheKey) return;
    let saved = loadPfmMindmap(cacheKey);
    if (!saved?.nodes.length) {
      const remote = await loadPfmMindmapRemote(this.sessionKey, cacheKey).catch(() => null);
      if (remote?.nodes.length) {
        savePfmMindmap(remote);
        saved = remote;
      }
    }
    const payload: ParsedPfmNodeList | null = saved?.nodes.length
      ? {
          title: saved.title || "",
          description: saved.description || "",
          nodes: saved.nodes,
        }
      : this._fallbackPayloadFromRunNodes();
    if (!payload?.nodes.length) return;
    const changed = JSON.stringify(payload) !== JSON.stringify(this._agentPayload);
    if (!changed) return;
    this._agentPayload = payload;
    this._agentTitle = payload.title || "";
    this._agentDescription = payload.description || "";
    this._warning = "";
    this._lastRenderedSignature = "";
    await this._renderMermaid();
  }

  /** LEGACY chat-only agent regenerate (no executionId). Run Explore must never reach this. */
  private async _regenerateMindmap(): Promise<void> {
    if (this._usingCommittedPath()) return;
    const sessionKey = String(this.sessionKey || "").trim();
    const cacheKey = this._cacheKey();
    // Top-level only: child levels use the report-panel's combined request.
    if (this._isChildLevel()) return;
    if (!sessionKey || !cacheKey || this._loading || this._pendingReqId) return;

    const reqId = generateReqId();
    const sentAtSec = Date.now() / 1000;
    const prompt = buildMarkeredPrompt(reqId);

    savePendingPfmRequest({ reqId, sessionKey: cacheKey, sentAtSec, prompt });
    this._pendingReqId = reqId;
    this._pendingSentAtSec = sentAtSec;
    this._pendingDeadlineMs = Date.now() + MAX_WAIT_MS;
    this._pendingTickMs = Date.now();
    this._loading = true;
    this._error = "";
    this._warning = "";

    let immediateAccepted = false;
    try {
      const persisted = await hermesClient.request("chat.send", {
        sessionKey,
        message: prompt,
        deliver: false,
      });
      if (!persisted) {
        this._warning = "Unable to save the PFM mindmap request. Please try Generate again.";
        this._finishPending();
        return;
      }
      const sendResp = await hermesClient.request<{ response?: string }>("chat.send", {
        sessionKey,
        message: buildWakePrompt(reqId),
        deliver: true,
      });
      immediateAccepted = this._tryAcceptReply(String(sendResp?.response || ""), reqId);
    } catch (error) {
      // chat.send raised (e.g. network) — fall through to the watcher.
      console.warn("[pfm-mindmap] chat.send failed; will watch chat history.", error);
    }

    if (immediateAccepted) {
      // _tryAcceptReply already cleared pending + loading.
      return;
    }

    // Synchronous response either timed out or did not contain a parseable list yet.
    // Start the background watcher to wait for the eventual assistant message.
    this._startReplyWatcher();
  }

  /**
   * Try to accept text as a reply for the given reqId. Prefers an exact
   * marker match; falls back to a parseable PFM node list (covers agents
   * that ignore the echo instruction). Returns true if accepted.
   */
  private _tryAcceptReply(text: string, reqId: string): boolean {
    const raw = String(text || "");
    if (!raw.trim()) return false;
    const markerMatch = raw.match(REPLY_MARKER_RE);
    if (markerMatch && markerMatch[1].toUpperCase() !== reqId.toUpperCase()) {
      return false;
    }
    const candidate = markerMatch ? stripReplyMarker(raw) : raw;
    const parsed = this._extractAgentPayload(candidate);
    if (parsed.nodes.length === 0) return false;
    const cacheKey = this._cacheKey();
    if (cacheKey) {
      const savedMindmap = {
        sessionKey: cacheKey,
        title: parsed.title,
        description: parsed.description,
        nodes: parsed.nodes,
        savedAt: Date.now(),
      };
      savePfmMindmap(savedMindmap);
      void persistPfmMindmapRemote(this.sessionKey, cacheKey, savedMindmap);
    }
    this._agentPayload = parsed;
    this._agentTitle = parsed.title;
    this._agentDescription = parsed.description;
    this._lastRenderedSignature = "";
    void this._renderMermaid();
    this._finishPending();
    return true;
  }

  private _startReplyWatcher(): void {
    this._stopReplyWatcher();
    if (!this._pendingReqId || !this._pendingSentAtSec) return;
    if (!this._pendingDeadlineMs) {
      this._pendingDeadlineMs = Date.now() + MAX_WAIT_MS;
    }
    this._pendingTickMs = Date.now();

    this._countdownTimer = setInterval(() => {
      this._pendingTickMs = Date.now();
    }, 1000);

    this._watcherTimer = setInterval(() => {
      void this._watcherTick();
    }, WATCHER_INTERVAL_MS);
    // Also kick once immediately so we don't wait a full interval after resume.
    void this._watcherTick();
  }

  private _stopReplyWatcher(): void {
    if (this._watcherTimer != null) {
      clearInterval(this._watcherTimer);
      this._watcherTimer = null;
    }
    if (this._countdownTimer != null) {
      clearInterval(this._countdownTimer);
      this._countdownTimer = null;
    }
  }

  private _finishPending(): void {
    const cacheKey = this._cacheKey();
    if (cacheKey) clearPendingPfmRequest(cacheKey);
    this._stopReplyWatcher();
    this._pendingReqId = "";
    this._pendingSentAtSec = 0;
    this._pendingDeadlineMs = 0;
    this._pendingTickMs = 0;
    this._loading = false;
  }

  private async _watcherTick(): Promise<void> {
    if (!canPollGateway()) return;
    const sessionKey = String(this.sessionKey || "").trim();
    const cacheKey = this._cacheKey();
    const reqId = this._pendingReqId;
    if (!sessionKey || !cacheKey || !reqId) {
      this._stopReplyWatcher();
      return;
    }
    if (Date.now() > this._pendingDeadlineMs) {
      this._warning = `Agent did not reply to ${reqId} within 5 minutes. Showing the previous mindmap.`;
      this._finishPending();
      return;
    }
    let messages: Array<{ role?: string; content?: string; timestamp?: number }> = [];
    try {
      messages = await runGatewayPoll(() => this._readLatestMessages(sessionKey));
    } catch {
      return;
    }
    const assistantMessages = messages.filter((m) => (m?.role || "") === "assistant");
    let requestMessageIndex = -1;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const msg = messages[i];
      if ((msg?.role || "") !== "user") continue;
      const marker = String(msg?.content || "").match(REQUEST_MARKER_RE);
      if (marker && marker[1].toUpperCase() === reqId.toUpperCase()) {
        requestMessageIndex = i;
        break;
      }
    }

    // Only accept the explicit marker match from history. Do not gate this by
    // timestamp: some Hermes history rows currently show bad 1970-era display
    // times, but the request id is already the exact correlation key.
    for (let i = assistantMessages.length - 1; i >= 0; i -= 1) {
      const content = String(assistantMessages[i]?.content || "");
      const marker = content.match(REPLY_MARKER_RE);
      if (marker && marker[1].toUpperCase() === reqId.toUpperCase()) {
        if (this._tryAcceptReply(content, reqId)) return;
      }
    }

    // If the agent omitted the reply marker, accept only a parseable assistant
    // message that appears after the persisted request in chat order.
    if (requestMessageIndex >= 0) {
      for (let i = messages.length - 1; i > requestMessageIndex; i -= 1) {
        const msg = messages[i];
        if ((msg?.role || "") !== "assistant") continue;
        if (this._tryAcceptReply(String(msg?.content || ""), reqId)) return;
      }
    }
  }

  private _resumePendingIfAny(): void {
    const cacheKey = this._cacheKey();
    if (!cacheKey) return;
    const pending = loadPendingPfmRequest(cacheKey);
    if (!pending) return;
    const elapsedMs = Date.now() - pending.sentAtSec * 1000;
    if (elapsedMs >= MAX_WAIT_MS) {
      clearPendingPfmRequest(cacheKey);
      return;
    }
    this._pendingReqId = pending.reqId;
    this._pendingSentAtSec = pending.sentAtSec;
    this._pendingDeadlineMs = pending.sentAtSec * 1000 + MAX_WAIT_MS;
    this._pendingTickMs = Date.now();
    this._loading = true;
    this._error = "";
    this._warning = "";
    this._startReplyWatcher();
  }

  private _cancelPending(): void {
    if (!this._pendingReqId) return;
    const reqId = this._pendingReqId;
    this._finishPending();
    this._warning = `Stopped waiting for ${reqId}. Click Generate to try again.`;
  }

  private _escapeMermaidQuotedLabel(value: string): string {
    const cleaned = String(value || "")
      // Keep generated labels inside Mermaid's id["label"] syntax so
      // punctuation is treated as text instead of mindmap grammar.
      .replace(/\r?\n+/g, " ")
      .replace(/-{3,}/g, " ")
      .replace(/_{3,}/g, " ")
      .replace(/={3,}/g, " ")
      .replace(/[`"]/g, "'")
      .replace(/[<>\\]/g, " ")
      .replace(/\s{2,}/g, " ")
      .trim();
    return cleaned || "Node";
  }

  private _toMindmapDefinition(payload: MermaidMapPayload): string {
    const nodes = payload.nodes ?? [];
    const nodeLine = (indent: number, id: string, label: string) =>
      `${"  ".repeat(indent)}${id}["${this._escapeMermaidQuotedLabel(label)}"]`;
    const rootLabel = this._mindmapRootLabel();
    if (!nodes.length) return `mindmap\n${nodeLine(1, "mm_root", rootLabel)}`;
    const byId = new Map<number, MermaidMapPayload["nodes"][number]>();
    const children = new Map<number | null, number[]>();
    const hasParent = new Set<number>();
    for (const n of nodes) {
      byId.set(n.node_id, n);
      const parent = n.parent_node_id ?? null;
      const list = children.get(parent) ?? [];
      list.push(n.node_id);
      children.set(parent, list);
      if (parent != null) hasParent.add(n.node_id);
    }

    const roots = children.get(null) ?? [];
    const topRoots = roots.length ? roots : nodes.map((n) => n.node_id);

    const lines: string[] = ["mindmap"];
    lines.push(nodeLine(1, "mm_root", rootLabel));
    const visited = new Set<number>();
    const walk = (id: number, depth: number) => {
      if (visited.has(id)) return;
      visited.add(id);
      const node = byId.get(id);
      if (!node) return;
      const label = node.title || node.node_key || `Node ${id}`;
      lines.push(nodeLine(depth, `mm_node_${id}`, label));
      const kidIds = children.get(id) ?? [];
      for (const kidId of kidIds) walk(kidId, depth + 1);
    };
    for (const id of topRoots) walk(id, 2);
    return lines.join("\n");
  }

  private _normalizeLabel(value: string): string {
    return String(value || "")
      .replace(/<br\s*\/?>/gi, " ")
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  }

  private _normalizeNodeKey(value: string): string {
    return String(value || "").trim().toLowerCase();
  }

  private _resolveRenderedNodeKey(rawText: string): string {
    const selected = this._renderedNodes.find((node) => {
      const label = this._normalizeLabel(node.label);
      const title = this._normalizeLabel(node.title);
      return rawText === label || rawText === title || rawText.includes(label) || rawText.includes(title);
    });
    return this._normalizeNodeKey(selected?.nodeKey || "");
  }

  private _mindmapRootLabel(): string {
    const explicit = String(this.rootLabel || "").trim();
    if (explicit) return this._formatCenterNodeLabel(explicit);
    const parentTitle = this.parentPath.length
      ? String(this.parentPath[this.parentPath.length - 1]?.title || "").trim()
      : "";
    if (parentTitle) return this._formatCenterNodeLabel(parentTitle);
    const derived = String(this._agentTitle || "PFM").trim();
    return this._formatCenterNodeLabel(derived || "PFM");
  }

  private _handleSvgClick(event: MouseEvent): void {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const group = target.closest("g");
    const rawText = this._normalizeLabel(group?.textContent || target.textContent || "");
    if (!rawText || rawText === this._normalizeLabel(this._mindmapRootLabel())) return;

    const selected = this._renderedNodes.find((node) => {
      const label = this._normalizeLabel(node.label);
      const title = this._normalizeLabel(node.title);
      return rawText === label || rawText === title || rawText.includes(label) || rawText.includes(title);
    });
    if (!selected) return;

    this.dispatchEvent(new CustomEvent("pfm-node-selected", {
      detail: {
        nodeKey: selected.nodeKey,
        title: selected.title,
        index: selected.index,
      },
      bubbles: true,
      composed: true,
    }));
  }

  private _fitSvgToPanel(svg: string): string {
    return String(svg || "").replace(/<svg\b([^>]*)>/i, (_match, attrs: string) => {
      let nextAttrs = String(attrs || "")
        .replace(/\swidth="[^"]*"/i, "")
        .replace(/\sheight="[^"]*"/i, "")
        .replace(/\sstyle="[^"]*"/i, "");
      if (!/\spreserveAspectRatio=/i.test(nextAttrs)) {
        nextAttrs += ' preserveAspectRatio="xMidYMid meet"';
      }
      return `<svg${nextAttrs} width="100%" style="width:100%;max-width:100%;display:block;">`;
    });
  }

  private _applySvgDecorations(): void {
    if (!this._svgRaw) return;
    let nextSvg = this._svgRaw;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(this._svgRaw, "image/svg+xml");
      const svgRoot = doc.querySelector("svg");
      if (svgRoot) {
        const rootLabelNorm = this._normalizeLabel(this._mindmapRootLabel());
        const visitedSet = new Set(
          (this.visitedNodeKeys || [])
            .map((nodeKey) => this._normalizeNodeKey(nodeKey))
            .filter(Boolean),
        );
        const activeSet = new Set(
          (this.activePathNodeKeys || [])
            .map((nodeKey) => this._normalizeNodeKey(nodeKey))
            .filter(Boolean),
        );
        const textNodes = Array.from(svgRoot.querySelectorAll("text"));
        for (const textNode of textNodes) {
          const rawText = this._normalizeLabel(textNode.textContent || "");
          if (!rawText || rawText === rootLabelNorm) continue;
          const matchedNodeKey = this._resolveRenderedNodeKey(rawText);
          if (!matchedNodeKey) continue;
          const nodeGroup = textNode.closest("g.node, g.mindmap-node, g[class*='node']");
          if (!nodeGroup) continue;
          if (visitedSet.has(matchedNodeKey)) nodeGroup.classList.add("node--visited-dim");
          if (activeSet.has(matchedNodeKey)) nodeGroup.classList.add("node--active-path");
        }
        nextSvg = new XMLSerializer().serializeToString(svgRoot);
      }
    } catch {
      nextSvg = this._svgRaw;
    }
    this._svg = this._fitSvgToPanel(nextSvg);
  }

  private async _renderMermaid(): Promise<void> {
    if (!this._agentPayload?.nodes.length) {
      this._svg = "";
      this._svgRaw = "";
      this._error = "";
      this._loading = false;
      return;
    }
    const signature = JSON.stringify(this._agentPayload.nodes.map((node) => [node.index, node.key, node.title]));
    if (signature === this._lastRenderedSignature && this._svg) return;
    this._loading = true;
    this._error = "";
    try {
      const payload = this._toPayload();
      const definition = this._sanitizeMermaidDefinition(this._toMindmapDefinition(payload));
      this._renderedNodes = payload.nodes.map((node, idx) => ({
        nodeKey: node.node_key,
        title: node.title,
        label: this._escapeMermaidQuotedLabel(node.title || node.node_key || `Node ${idx + 1}`),
        index: idx + 1,
      }));
      const svg = await renderMermaidToSvg(definition, "pfm-run-map");
      this._svgRaw = svg || "";
      this._applySvgDecorations();
      this._lastRenderedSignature = signature;
    } catch (error) {
      this._svg = "";
      this._svgRaw = "";
      this._lastRenderedSignature = "";
      this._error = error instanceof Error ? error.message : "Failed to render Mermaid mindmap.";
    } finally {
      this._loading = false;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ead-pfm-mindmap-mermaid": EadPfmMindmapMermaid;
  }
}
